import type { HelpContent, HelpKey } from './entries.tr';
import { HELP_FAZ8_EN } from './entries.faz8.en';
import { AI_HELP_EN } from './entries.ai';

type En = Pick<HelpContent, 'title' | 'short'> & Partial<Pick<HelpContent, 'whatItDoes'>>;

/** English titles and short descriptions. Longer fields fall back to Turkish until translated. */
export const HELP_EN: Record<HelpKey, En> = {
  ...HELP_FAZ8_EN,
  ...AI_HELP_EN,
  'page.dashboard': {
    title: 'Dashboard',
    short: 'Shows your business at a glance.',
    whatItDoes:
      'Setup progress, system status and alerts; with the sales reports permission, today’s and the last 30 days’ sales with a daily chart. No sample numbers are ever shown.',
  },
  'page.setup': { title: 'Setup Wizard', short: 'The 24 steps needed to go live, in order.' },
  'page.organization': { title: 'Business Details', short: 'Legal name, tax and contact details.' },
  'page.brands': { title: 'Brands', short: 'The brands you sell (e.g. Zootopia Global, VITATOPIA).' },
  'page.stores': { title: 'Stores', short: 'Sales channels: domains and country/language/currency markets.' },
  'page.users': { title: 'Users', short: 'Staff with panel access and their roles.' },
  'page.roles': { title: 'Roles & Permissions', short: 'What each role can do; create custom roles.' },
  'page.api_keys': {
    title: 'API Keys',
    short: 'Keys for external programs (accounting, ERP, n8n) to connect.',
  },
  'page.webhooks': { title: 'Webhooks', short: 'Notify another program instantly when something happens.' },
  'page.integrations': { title: 'Integrations', short: 'Real connection status of external services.' },
  'page.audit': { title: 'Audit Log', short: 'Who changed what and when, with old and new values.' },
  'page.system': { title: 'System Status', short: 'Database, queues, email, SSL and backups.' },
  'page.privacy': {
    title: 'Data subject requests',
    short: 'Records, tracks and resolves personal data requests within the 30-day legal deadline.',
  },
  'page.migration': {
    title: 'Migration package',
    short: 'Moves a store prepared on staging to live (or a second store) with one file.',
  },
  'page.account': { title: 'My Account', short: 'Profile, password, language and open sessions.' },
  'page.help_editor': { title: 'Help Texts', short: 'Edit the explanations shown in the panel.' },
  'auth.sign_in': {
    title: 'Sign in',
    short: 'Signs in with email and password; a verification code follows.',
  },
  'auth.mfa_enroll': {
    title: 'Set up two-step verification',
    short: 'Links the authenticator app on your phone to your account.',
  },
  'auth.mfa_verify': {
    title: 'Verification code',
    short: 'Enter the 6-digit code from your authenticator app.',
  },
  'auth.backup_codes': {
    title: 'Backup codes',
    short: '10 one-time codes for when you cannot reach your phone.',
  },
  'auth.forgot_password': { title: 'Forgot password', short: 'Emails a reset link valid for 1 hour.' },
  'organization.save': { title: 'Save business details', short: 'Saves your changes.' },
  'organization.tax_number': {
    title: 'Tax number / national ID',
    short: '10-digit tax number or 11-digit national ID.',
  },
  'organization.mersis': { title: 'MERSIS number', short: '16-digit central registry number.' },
  'organization.kep': { title: 'KEP address', short: 'Registered electronic mail address (if any).' },
  'stores.return_policy': {
    title: 'Return policy (for search engines)',
    short: 'Return window, method and fees reported to Google; must match your legal texts.',
  },
  'stores.return_days': { title: 'Return window (days)', short: 'At least 14 days for distance sales.' },
  'stores.return_method': { title: 'Return method', short: 'By mail or in store.' },
  'stores.return_fees': { title: 'Return shipping fee', short: 'Free only if you pay return shipping.' },
  'shipping.handling_days': {
    title: 'Handling time (business days)',
    short: 'From order to handing over to the carrier.',
  },
  'shipping.transit_days': {
    title: 'Transit time (business days)',
    short: 'Time in transit with the carrier.',
  },
  'organization.etbis': {
    title: 'ETBİS verification link',
    short: 'The ETBİS address opened by your registration QR code; shown in the storefront footer.',
  },
  'brands.create': { title: 'Add brand', short: 'Creates a new brand.' },
  'brands.edit': { title: 'Edit brand', short: 'Changes name, description, color or logo.' },
  'brands.archive': { title: 'Archive', short: 'Hides the brand without deleting it.' },
  'brands.logo_upload': { title: 'Upload logo', short: 'PNG, JPEG, WebP, AVIF or GIF; max 5 MB.' },
  'brands.slug': { title: 'Short name', short: 'Name used in URLs: lowercase letters, digits and hyphens.' },
  'brands.primary_color': { title: 'Primary color', short: 'Main brand color (e.g. #0F766E).' },
  'stores.create': { title: 'Add store', short: 'Creates a new sales channel.' },
  'stores.edit': { title: 'Edit store', short: 'Changes name, channel type and brands.' },
  'stores.channel_type': { title: 'Channel type', short: 'B2C, B2B or hybrid (both).' },
  'stores.status': {
    title: 'Publication status',
    short:
      'Preview, Live or Maintenance. Going live needs the readiness list, explicit confirmation and two-step verification.',
  },
  'stores.go_live': { title: 'Take the store live', short: 'Opens the storefront to real customers.' },
  'stores.maintenance': {
    title: 'Maintenance',
    short: 'Temporarily closes the storefront with a maintenance page (503).',
  },
  'stores.to_draft': { title: 'Back to preview', short: 'Takes the store offline into preview.' },
  'stores.go_live_confirm': {
    title: 'Go-live confirmation',
    short: 'You confirm prices, payment methods and legal texts were checked.',
  },
  'stores.status_note': { title: 'Note', short: 'Reason for the change; written to the audit log.' },
  'stores.add_domain': { title: 'Add domain', short: 'Connects a domain to the store.' },
  'stores.remove_domain': { title: 'Remove domain', short: 'Detaches the domain from the store.' },
  'stores.add_market': { title: 'Add market', short: 'Adds a country + language + currency combination.' },
  'stores.default_market': {
    title: 'Make default',
    short: 'The storefront opens in this market’s language and currency.',
  },
  'stores.remove_market': {
    title: 'Remove market',
    short: 'Deletes the market. The default market cannot be removed.',
  },
  'users.copy_invite_link': {
    title: 'Copy link',
    short: 'Copies the invitation link so you can share it via WhatsApp, SMS or in person.',
  },
  'users.invite': {
    title: 'Invite user',
    short: 'Creates an invitation link valid for 7 days; emails it if the email service is set up.',
  },
  'users.change_role': { title: 'Change role', short: 'Changes the role that defines permissions.' },
  'users.store_scope': {
    title: 'Store restriction',
    short: 'Empty = all stores; otherwise only selected stores.',
  },
  'users.dealer_scope': {
    title: 'Dealer scope',
    short:
      '"Assigned dealers only": the user sees dealers assigned to them or in regions they manage, with their users, orders, quotes and statements; retail customers and other dealers stay hidden.',
  },
  'users.suspend': { title: 'Suspend', short: 'Stops panel access; records are kept.' },
  'users.remove': { title: 'Remove member', short: 'Removes the person from this business.' },
  'users.revoke_invitation': { title: 'Revoke invitation', short: 'Invalidates a pending invitation.' },
  'roles.create_custom': {
    title: 'Create custom role',
    short: 'Defines a new role permission by permission.',
  },
  'roles.edit_custom': {
    title: 'Edit custom role',
    short: 'Changes the role permissions for everyone using it.',
  },
  'roles.delete_custom': { title: 'Delete custom role', short: 'Deletes the role.' },
  'api_keys.create': {
    title: 'Create API key',
    short: 'Generates a key for an external program to connect.',
  },
  'api_keys.scopes': { title: 'Scopes', short: 'What the key may do. Select only what is needed.' },
  'api_keys.revoke': { title: 'Revoke', short: 'Invalidates the key immediately.' },
  'webhooks.create': { title: 'Add webhook', short: 'Registers a URL to notify on selected events.' },
  'webhooks.events': { title: 'Events', short: 'Which events trigger a notification. "*" means all.' },
  'webhooks.test': {
    title: 'Send test event',
    short: 'Sends a "webhook.test" event to all active webhooks.',
  },
  'webhooks.rotate_secret': {
    title: 'Rotate secret',
    short: 'Generates a new signing secret; the old one stops working.',
  },
  'webhooks.redeliver': { title: 'Redeliver', short: 'Retries a failed delivery now.' },
  'webhooks.delete': { title: 'Delete webhook', short: 'Stops notifications to this URL.' },
  'integrations.save_credentials': {
    title: 'Save credentials',
    short: 'Stores the details encrypted. Status stays "Setup required" until tested.',
  },
  'integrations.test': {
    title: 'Test connection',
    short: 'Tries to connect to the real service with the saved details.',
  },
  'integrations.disconnect': {
    title: 'Disconnect',
    short: 'Deletes saved details and disables the integration.',
  },
  'integrations.environment': {
    title: 'Environment',
    short: 'Sandbox: no real money. Live: real transactions.',
  },
  'audit.filter': { title: 'Filter', short: 'Filters records by type (e.g. only store changes).' },
  'system.refresh': { title: 'Refresh', short: 'Measures the status again.' },
  'setup.skip_step': { title: 'Skip step', short: 'Marks this step as not needed for now.' },
  'account.change_password': {
    title: 'Change password',
    short: 'At least 12 characters. Other sessions are signed out.',
  },
  'account.sessions_revoke': { title: 'Sign out session', short: 'Ends the session on the selected device.' },
  'account.ui_mode': {
    title: 'Usage mode',
    short: 'Beginner shows explanations; advanced keeps the screen clean.',
  },
  'account.language': { title: 'Panel language', short: 'Türkçe, English, Deutsch or العربية.' },
  'help.edit': { title: 'Edit explanation', short: 'Changes this help text; the old version is kept.' },
  'page.products': {
    title: 'Products',
    short: 'All products: variants, stock, vet review and publishing status.',
  },
  'page.product_editor': {
    title: 'Edit product',
    short: 'Details, content, variants, images, prices and publishing.',
  },
  'page.categories': { title: 'Categories', short: 'Product groups on the storefront.' },
  'page.pricing': {
    title: 'Pricing',
    short: 'Customer groups, price lists, tax classes and bulk price changes.',
  },
  'page.discounts': { title: 'Coupons', short: 'Percentage or fixed discounts applied with a code.' },
  'page.stock': { title: 'Stock', short: 'Warehouses, variant stock, lots / expiry dates and movements.' },
  'page.customers': { title: 'Customers', short: 'Storefront members and dealer users.' },
  'page.customer_detail': {
    title: 'Customer',
    short: 'Profile, companies, consents, tags, order summary (RFM), addresses and orders.',
  },
  'page.dealers': { title: 'Dealers', short: 'Dealer companies, statuses and price groups.' },
  'page.dealer_detail': {
    title: 'Dealer',
    short: 'Company details, status actions, users and applications.',
  },
  'page.dealer_applications': {
    title: 'Dealer applications',
    short: 'Applications submitted on the storefront.',
  },
  'page.imports': {
    title: 'Import / export',
    short: 'Bulk upload and download of products and dealers (Excel/CSV).',
  },
  'page.orders': { title: 'Orders', short: 'Orders from the storefront, dealers and the panel.' },
  'page.order_detail': { title: 'Order', short: 'Lines, totals, addresses, timeline and status actions.' },
  'products.create': { title: 'New product', short: 'Creates a draft product.' },
  'products.slug': {
    title: 'Short name (URL)',
    short: 'The product page address. Generated from the title if empty.',
  },
  'products.species': { title: 'Species', short: 'Which animals the product is for.' },
  'products.tax_class': { title: 'Tax class', short: 'Empty uses the default tax class.' },
  'products.status': { title: 'Status', short: 'Draft · Active (sellable) · Archived.' },
  'products.visibility': { title: 'Visible to', short: 'Retail customers and/or approved dealers.' },
  'products.dealer_min_quantity': {
    title: 'Dealer minimum quantity',
    short:
      'Dealers order at least this many of a variant (not retail). Rounded up to the case size. Empty: no rule.',
  },
  'products.dealer_pack_size': {
    title: 'Dealer case size',
    short:
      'Dealers order in multiples of this quantity (e.g. 12, 24). Shown on the product page and cart. Empty: no rule.',
  },
  'pricing.group_min_order': {
    title: 'Minimum order amount',
    short: 'Dealers in this group cannot order below this cart total (shipping excluded). Empty: none.',
  },
  'dealers.po_requirement': {
    title: 'Purchase order (PO) requirement',
    short:
      'Storefront orders need a PO number, or a number and a document. Not required for orders you create in the panel.',
  },
  'dealers.min_order': {
    title: 'Dealer minimum order',
    short: 'Overrides the group amount for this dealer. Empty: the dealer group amount applies.',
  },
  'products.health_content': {
    title: 'Health content',
    short: 'Requires vet reviewer approval before publishing.',
  },
  'products.feed_registration_no': {
    title: 'Feed registration no',
    short: 'Ministry registration/approval number.',
  },
  'products.variants': {
    title: 'Options and variants',
    short: 'Each option combination is a variant with its own SKU and stock.',
  },
  'products.sku': { title: 'SKU', short: 'Unique stock code.' },
  'products.cost': { title: 'Cost', short: 'Purchase cost; visible only with the cost permission.' },
  'products.vet_review': { title: 'Vet review', short: 'The vet reviewer approves or rejects the content.' },
  'products.media_upload': { title: 'Add image', short: 'PNG/JPEG/WebP up to 5 MB; converted to WebP.' },
  'products.prices': {
    title: 'Product prices',
    short: 'Price in each price list; saved when you leave the field.',
  },
  'products.publish': { title: 'Publish', short: 'Shows the product on the selected store.' },
  'categories.parent': { title: 'Parent category', short: 'Empty means top level.' },
  'pricing.group_kind': { title: 'Group type', short: 'Dealer groups hold approved dealer companies.' },
  'pricing.list_group': {
    title: 'List group',
    short: 'Empty: base list for everyone; a group limits it to that group.',
  },
  'pricing.tax_inclusive': {
    title: 'Prices include tax',
    short: 'Dealer lists may be entered excluding tax.',
  },
  'pricing.priority': { title: 'Priority', short: 'Higher priority wins when several lists apply.' },
  'pricing.tax_class': { title: 'Tax classes', short: 'Tax rates assigned to products.' },
  'pricing.bulk_preview': {
    title: 'Bulk price preview',
    short: 'Calculates new prices without changing anything.',
  },
  'pricing.bulk_apply': {
    title: 'Apply bulk prices',
    short: 'Writes the previewed prices (requires recent 2FA).',
  },
  'pricing.bulk_revert': {
    title: 'Revert bulk prices',
    short: 'Restores old prices; manual changes are kept.',
  },
  'pricing.price_ending': { title: 'Price ending', short: 'Rounds up to whole, .90 or .99.' },
  'discounts.code': { title: 'Coupon code', short: '3–32 characters; letters, digits, - and _.' },
  'discounts.min_subtotal': {
    title: 'Minimum cart total',
    short: 'The coupon does not apply below this subtotal.',
  },
  'discounts.usage_limit': { title: 'Usage limit', short: 'How many orders can use it in total.' },
  'discounts.companies': {
    title: 'Dealer companies',
    short: 'Dealer-only coupon: only users of the selected approved dealer companies can use the code.',
  },
  'discounts.groups': { title: 'Valid groups', short: 'Empty means everyone.' },
  'stock.change_type': { title: 'Movement type', short: 'Receipt, adjustment, damage or count.' },
  'stock.lot': {
    title: 'Lot and expiry',
    short: 'Earliest expiry is sold first; expired lots are not sold.',
  },
  'stock.transfer': { title: 'Transfer', short: 'Moves stock between warehouses.' },
  'stock.expiring': { title: 'Expiring soon', short: 'Lots with stock expiring within the selected days.' },
  'customers.pii_masked': {
    title: 'Masked personal data',
    short: 'Email, phone and address are masked without the PII permission.',
  },
  'customers.access_link': {
    title: 'Access link',
    short: 'Activation or password reset link; shown on screen if email is not set up.',
  },
  'customers.unlock': { title: 'Unlock', short: 'Unlocks an account locked after failed sign-ins.' },
  'customers.consents': { title: 'Marketing consents', short: 'Email/SMS consent history; never deleted.' },
  'dealers.tax_number': { title: 'Tax no / national ID', short: 'Stored encrypted; must be unique.' },
  'dealers.external_code': {
    title: 'Dealer code',
    short: 'Code from your previous system; used for matching on import.',
  },
  'dealers.group': { title: 'Dealer group', short: 'Decides which price list the dealer sees.' },
  'dealers.status_action': { title: 'Dealer status', short: 'Approve, suspend or reject.' },
  'dealers.add_member': {
    title: 'Add user',
    short: 'Links a dealer employee and creates an activation link.',
  },
  'applications.documents': {
    title: 'Documents',
    short: 'Private files; only authorised panel users can download.',
  },
  'applications.decision': {
    title: 'Decision',
    short: 'Choose a dealer group to approve; give a reason to reject.',
  },
  'imports.products_file': {
    title: 'Upload product file',
    short: 'Our template, a Shopify product CSV or a product JSON/XML document.',
  },
  'imports.dealers_file': {
    title: 'Upload dealer list',
    short: 'Excel/CSV with company name and dealer code or tax no.',
  },
  'imports.price_list': { title: 'Target price list', short: 'The "Price" column is written to this list.' },
  'imports.warehouse': { title: 'Target warehouse', short: 'The "Stock" column is written here as a count.' },
  'imports.default_status': { title: 'Default status', short: 'Used when the status column is empty.' },
  'imports.send_links': { title: 'Activation email', short: 'Sends set-password links if email is set up.' },
  'imports.download_issues': {
    title: 'Download issue list',
    short: 'Downloads the preview issues as CSV with row numbers.',
  },
  'imports.export': {
    title: 'Export',
    short: 'Downloads the same columns as Excel, or a JSON/XML document.',
  },
  'orders.ledger_settled': {
    title: 'Settled on account',
    short: "The open-account debt was fully covered by payments recorded on the dealer's account.",
  },
  'orders.manual_create': { title: 'Create order', short: "Creates an order at the customer's price." },
  'page.order_print': {
    title: 'Print orders',
    short: 'Order summary or packing list of the selected orders.',
  },
  'orders.guest': {
    title: 'Customer type',
    short: 'Registered customer (their group price) or guest (retail price; email required).',
  },
  'orders.shipping_fee': {
    title: 'Shipping',
    short: 'Fee from the shipping method, or a manual fee with pricing permission.',
  },
  'orders.manual_discount': { title: 'Discount', short: 'Coupon code or a manual amount; not both.' },
  'orders.edit_draft': {
    title: 'Edit draft',
    short: 'Everything can change on a draft; prices are recalculated.',
  },
  'orders.edit_lines': { title: 'Edit items', short: 'Items and quantities can change before payment.' },
  'orders.edit_address': { title: 'Edit address and note', short: 'Until a shipment is created.' },
  'orders.payment_link': {
    title: 'Payment link',
    short: 'Creates a time-limited link the customer uses to pay the order by card.',
  },
  'orders.payment_link_hours': {
    title: 'Valid for',
    short: 'After this, the link no longer accepts payment.',
  },
  'orders.payment_link_email': {
    title: 'Send by email',
    short: 'Sends the link to the order’s email address.',
  },
  'orders.bulk_status': {
    title: 'Bulk status change',
    short: 'Each selected order changes under its own rules.',
  },
  'orders.bulk_print': {
    title: 'Print',
    short: 'Opens the order summary or packing list of the selected orders.',
  },
  'orders.bulk_export': {
    title: 'Download as Excel',
    short: 'Exports the selected orders with masked personal data.',
  },
  'orders.print': { title: 'Print / save as PDF', short: 'Opens the browser print dialog.' },
  'orders.status_change': {
    title: 'Change status',
    short: 'Moves the order forward; cancelling releases stock.',
  },
  'orders.payment_note': { title: 'Payment note', short: 'Describe how payment was received.' },
  'orders.notes': { title: 'Internal note', short: 'Visible only in the panel.' },
  'orders.po': {
    title: 'Purchase order and company approval',
    short:
      'The dealer’s purchase order (PO) number and document; who approved a buyer’s order above their threshold.',
  },
  'orders.po_number': {
    title: 'PO number',
    short: 'The dealer’s own purchase order number (up to 50 characters). Orders can be searched by it.',
  },
  'orders.po_document': {
    title: 'Attach or replace PO document',
    short:
      'PDF, JPEG or PNG (up to 10 MB). Stored privately; only panel users who can see orders download it.',
  },
  'orders.company_approval': {
    title: 'Company approval',
    short: 'The company owner approved the buyer’s cart above their threshold on the storefront.',
  },
  'page.payment_settings': {
    title: 'Payment Settings (POS Setup)',
    short:
      'Card payments (iyzico/PayTR), bank transfer, cash on delivery, dealer open account; go-live checklist.',
  },
  'page.payments': {
    title: 'Payments',
    short: 'All payment attempts, captures, refunds and provider reconciliation.',
  },
  'page.invoices': { title: 'Invoices', short: 'Order invoices sent to BizimHesap or recorded manually.' },
  'page.redirects': {
    title: 'Redirects',
    short: 'Points old addresses to new ones (301 permanent, 302 temporary).',
  },
  'redirects.add': { title: 'Add redirect', short: 'Points one old address to a new one.' },
  'redirects.import': {
    title: 'Import from file',
    short: 'Bulk-loads the old site address list (CSV/Excel); previewed first.',
  },
  'redirects.search': { title: 'Search addresses', short: 'Type part of an old or new address.' },
  'redirects.from': { title: 'Old address', short: 'Path to redirect, starting with "/".' },
  'redirects.to': {
    title: 'New address',
    short: 'Path on this site or a full address starting with https://.',
  },
  'redirects.code': {
    title: 'Redirect type',
    short: '301 is permanent and tells search engines the page moved; 302 is temporary.',
  },
  'redirects.note': { title: 'Note', short: 'A note for yourself.' },
  'redirects.template': { title: 'Download template', short: 'Empty CSV template with the right headers.' },
  'page.shipping': { title: 'Shipping Methods', short: 'Shipping options and prices shown at checkout.' },
  'page.legal': {
    title: 'Legal Texts',
    short:
      'Privacy notice, membership and distance sales contract, pre-information form, returns, privacy and cookie policy.',
  },
  'payments.card_provider': {
    title: 'Card provider',
    short: 'iyzico, PayTR or Sipay; the connection test must pass for the selected environment.',
  },
  'payments.environment': {
    title: 'Environment',
    short: 'No real money is charged in test (sandbox). Going live needs a separate confirmation.',
  },
  'payments.max_installments': { title: 'Installments', short: 'Maximum installments offered to customers.' },
  'payments.card_capture': {
    title: 'When to charge',
    short: 'Pre-authorize at checkout and charge when shipping (iyzico only).',
  },
  'orders.preauth_capture': {
    title: 'Capture',
    short: 'Charges the current order total from the card hold.',
  },
  'orders.preauth_release': {
    title: 'Release pre-authorization',
    short: 'Releases the card hold and cancels the order.',
  },
  'payments.card_timeout': {
    title: 'Card payment timeout',
    short: 'Unfinished card payments are cancelled after this time; stock is released.',
  },
  'payments.bank_transfer_hold': {
    title: 'Bank transfer payment window',
    short:
      'Bank transfer orders not paid within this time are cancelled automatically and stock is released.',
  },
  'payments.notify_urls': {
    title: 'Notification URLs',
    short: 'Addresses to enter as callback/notification URL in the provider panels.',
  },
  'payments.other_methods': {
    title: 'Other payment methods',
    short: 'Bank transfer, cash on delivery, dealer open account and guest checkout.',
  },
  'payments.cod_fee': {
    title: 'Cash on delivery fee',
    short: 'Service fee added to the order (tax included).',
  },
  'payments.cod_max': {
    title: 'Cash on delivery limit',
    short: 'Not offered above this order total. Empty: no limit.',
  },
  'payments.bank_account': {
    title: 'Bank account',
    short: 'IBANs shown for bank transfer; changes are sensitive and audited.',
  },
  'payments.iban': { title: 'IBAN', short: '26 characters starting with TR.' },
  'payments.reconcile': {
    title: 'Reconciliation',
    short: "Compares the provider's daily report with your payments.",
  },
  'shipping.method': { title: 'Shipping method', short: 'Adds or edits a checkout shipping option.' },
  'shipping.carrier': { title: 'Carrier', short: 'Shown in tracking links and emails; no API needed.' },
  'shipping.audience': {
    title: 'Audience',
    short: 'Everyone, retail customers only or approved dealers only.',
  },
  'shipping.rate_type': { title: 'Rate type', short: 'Flat rate or by cart weight tiers.' },
  'shipping.free_over': {
    title: 'Free shipping over',
    short: 'Shipping is free once the goods total reaches this amount.',
  },
  'legal.variables': {
    title: 'Variables',
    short: '{{field}} placeholders are filled with real data when shown.',
  },
  'legal.languages': {
    title: 'Language versions',
    short:
      'Each text has its own draft, publication and legal review declaration per language. Turkish is the primary version.',
  },
  'legal.language': {
    title: 'Text language',
    short: 'The language you are editing. Each language is saved and published separately.',
  },
  'legal.copy_tr': {
    title: 'Start from the Turkish text',
    short: 'Copies the Turkish text (with its variables) into this language’s draft so you can translate it.',
  },
  'legal.reviewed': { title: 'Legal review', short: 'Declares that a lawyer reviewed the text.' },
  'legal.publish': {
    title: 'Publish legal text',
    short: 'Publishes the saved draft of this language as a new version; the previous version is archived.',
  },
  'invoices.send': {
    title: 'Send to BizimHesap',
    short: 'Sends the invoice to BizimHesap; becomes "uncertain" on timeout.',
  },
  'invoices.uncertain': {
    title: 'Uncertain invoice',
    short: 'Check BizimHesap and record whether the invoice was created.',
  },
  'invoices.create': {
    title: 'Create invoice',
    short: 'Draft invoice for BizimHesap or a record of an invoice issued elsewhere.',
  },
  'orders.capture': {
    title: 'Payment received',
    short: 'Records a bank transfer, cash on delivery or open account payment.',
  },
  'orders.refund': {
    title: 'Refund',
    short: 'Card refunds go through the provider; others record a manual refund.',
  },
  'orders.resolve_refund': {
    title: 'Set refund outcome',
    short: 'Closes an interrupted refund as made or not made after checking the provider panel.',
  },
  'orders.resolve_refund_outcome': {
    title: 'Outcome in provider panel',
    short: 'Whether this refund appears in the iyzico or PayTR transaction list.',
  },
  'orders.resolve_refund_note': {
    title: 'Note',
    short: 'What you checked to decide; written to the audit log.',
  },
  'orders.returns': {
    title: 'Return / withdrawal requests',
    short:
      'Return, withdrawal and exchange requests customers open from their order page; approving does not refund.',
  },
  'orders.address_place': {
    title: 'Province and district',
    short:
      'For addresses in Türkiye, choose the province and district from the list. A postal code must start with the province code.',
  },
  'orders.cancel_items': {
    title: 'Cancel remaining items',
    short:
      'Cancels items of a partly shipped order that cannot ship; reserved stock is freed and the customer is emailed.',
  },
  'orders.exchange_ship': {
    title: 'Ship exchange',
    short:
      'Once the returned item arrives, ships the new item with a tracking number; the reserved item leaves stock and the request completes.',
  },
  'orders.return_note': {
    title: 'Note / reason',
    short: 'Emailed to the customer and shown on their order page. Required for reject and cancel.',
  },
  'orders.restock': { title: 'Restock', short: 'Adds returned items back to the same lot.' },
  'orders.ship': {
    title: 'Ship',
    short:
      'Creates a shipment with a tracking number; the packed quantities leave stock. With nothing left to ship, the order becomes shipped.',
  },
  'orders.ship_lines': {
    title: 'Items in this package',
    short:
      'Quantities shipped in this package. Defaults to everything not yet shipped; set an item to 0 to leave it for a later package.',
  },
  'orders.manual_payment_method': {
    title: 'Payment method',
    short: 'Manual/bank transfer: mark "Payment received" later. Open account/COD: prepared directly.',
  },
  'page.page_designs': {
    title: 'Page designs',
    short: 'Chooses one of three design options for the storefront top menu and Articles page.',
  },
  'page_designs.header': {
    title: 'Top menu design',
    short: 'Layout of the logo, menu items, search and cart in the header.',
  },
  'page_designs.articles': {
    title: 'Articles design',
    short: 'Look of the blog list, category and archive pages.',
  },
  'page_designs.preview': {
    title: 'Preview',
    short: 'Opens the option on the storefront in your browser only, without publishing.',
  },
  'page_designs.publish': { title: 'Publish', short: 'Makes the option live for all visitors immediately.' },
  'page_designs.history': {
    title: 'Change history',
    short: 'The last 30 option changes: date, person, experience and change.',
  },
  'page.sales_points': {
    title: 'Store listings',
    short: 'Manages the dealers listed on the storefront Store locator page.',
  },
  'dealers.sales_points': {
    title: 'Store listing',
    short: "This dealer's business details on the Store locator page.",
  },
  'sales_points.auto_add': {
    title: 'Automatic listing',
    short: 'Newly approved dealers are listed from their locations (on by default).',
  },
  'sales_points.visible': {
    title: 'Show on storefront',
    short: 'When off, the listing is hidden on the storefront; the record is kept.',
  },
  'sales_points.name': { title: 'Display name', short: 'The shop or business name shown on the storefront.' },
  'sales_points.neighbourhood': {
    title: 'Neighbourhood',
    short: 'Neighbourhoods from the PTT list; visitors can filter by neighbourhood.',
  },
  'sales_points.pin': {
    title: 'Location (map pin)',
    short: 'Coordinates or a link copied from Google Maps.',
  },
  'sales_points.phone': { title: 'Storefront phone', short: 'The business number visitors will call.' },
  'sales_points.hours': {
    title: 'Opening hours',
    short: 'Open/closed and hours for each day; unspecified days are not shown.',
  },
  'sales_points.note': {
    title: 'Note',
    short: 'A short note shown under the card (e.g. "Parking available").',
  },
  'sales_points.bulk': {
    title: 'Bulk show/hide',
    short: 'Shows or hides the selected listings; recorded in the audit log.',
  },
  'sales_points.from_location': {
    title: 'Add from location',
    short: "Creates a listing from one of the dealer's locations.",
  },
  'sales_points.geocode': {
    title: 'Find location from address',
    short:
      'Locations without a manual pin are looked up from their address via OpenStreetMap (on by default).',
  },
  'sales_points.backfill': {
    title: 'Create from approved dealers',
    short:
      'Adds a visible listing for every approved dealer without one, from its locations or company address.',
  },
  'sales_points.geocode_now': {
    title: 'Find locations now',
    short: 'Looks up the address of up to 10 pending listings right away (one request per second).',
  },
  'sales_points.geocode_one': {
    title: 'Find location from address',
    short: 'Looks up this listing on OpenStreetMap and saves the result as its pin.',
  },
  'page_designs.store_locator': {
    title: 'Store locator design',
    short: 'Layout of the Store locator page and province/district pages.',
  },
  'page.design': {
    title: 'Design',
    short: 'Change storefront colours, fonts and layout without code.',
    whatItDoes:
      'Changes stay in a draft and show instantly in the live preview. "Preview on storefront" shows the draft only to you. "Publish" makes it public and stores an immutable version; you can go back any time. Unreadable colour pairs (WCAG AA contrast) block publishing.',
  },
  'design.preview': {
    title: 'Preview on storefront',
    short: 'Shows the draft on the real storefront, only to you (link valid for 2 hours).',
  },
  'design.save': {
    title: 'Save draft',
    short: 'Stores the changes as a draft; the storefront does not change.',
  },
  'design.publish': { title: 'Publish', short: 'Makes the draft public and stores it as a new version.' },
  'design.revert_local': {
    title: 'Discard my changes',
    short: 'Drops unsaved changes and shows the saved draft.',
  },
  'design.discard': {
    title: 'Revert to the live theme',
    short: 'Deletes unpublished draft changes; the draft equals the live version.',
  },
  'design.templates': {
    title: 'Templates',
    short: 'Ready starting points that change colours, fonts and layout together.',
  },
  'design.palettes': {
    title: 'Ready palettes',
    short: 'Contrast-checked colour sets; fonts and layout are kept.',
  },
  'design.color.background': { title: 'Background', short: 'Main page background.' },
  'design.color.surface': {
    title: 'Card and field background',
    short: 'Background of product cards, header, summaries and fields.',
  },
  'design.color.text': { title: 'Text', short: 'Body and heading colour; needs at least 4.5:1 contrast.' },
  'design.color.muted_text': {
    title: 'Secondary text',
    short: 'Descriptions, brand names and dates; needs at least 4.5:1 contrast.',
  },
  'design.color.border': { title: 'Lines and borders', short: 'Card borders and dividers.' },
  'design.color.primary': {
    title: 'Primary colour',
    short: 'Links, selected tabs and highlights; needs at least 4.5:1 contrast.',
  },
  'design.color.secondary': {
    title: 'Secondary colour',
    short: 'Secondary labels and badges; text colour is chosen automatically.',
  },
  'design.color.accent': {
    title: 'Accent colour',
    short: 'Campaign and discount labels; text colour is chosen automatically.',
  },
  'design.color.button': { title: 'Button', short: 'Colour of main buttons such as "Add to cart".' },
  'design.color.button_text': {
    title: 'Button text',
    short: 'Text on main buttons; needs at least 4.5:1 contrast.',
  },
  'design.color.button_hover': {
    title: 'Button (hover)',
    short: 'Button colour while the pointer is over it.',
  },
  'design.dark_mode': {
    title: 'Dark appearance',
    short: 'Uses separate dark colours when the visitor’s device is in dark mode.',
  },
  'design.font_body': {
    title: 'Body font',
    short: 'Font for text, prices and forms. Served with the storefront.',
  },
  'design.font_heading': { title: 'Heading font', short: 'Font for page and product headings.' },
  'design.base_size': { title: 'Body text size', short: 'Base text size; headings scale from it.' },
  'design.heading_scale': { title: 'Heading sizes', short: 'How much larger headings are than body text.' },
  'design.heading_weight': { title: 'Heading weight', short: 'Thickness of headings.' },
  'design.radius': { title: 'Corner radius', short: 'Corners of cards, fields and images (0 = square).' },
  'design.button_shape': { title: 'Button shape', short: 'Square, rounded or pill-shaped buttons.' },
  'design.shadow': { title: 'Shadow', short: 'Shadow used on raised cards and summary boxes.' },
  'design.card_style': { title: 'Product card', short: 'Bordered, flat or raised product cards.' },
  'design.image_ratio': { title: 'Product image ratio', short: 'Shape of the image area in product lists.' },
  'design.grid_desktop': {
    title: 'Columns on desktop',
    short: 'Products per row on wide screens; tablets use at most 3.',
  },
  'design.grid_mobile': { title: 'Columns on phones', short: 'One or two products per row on phones.' },
  'design.header_layout': { title: 'Logo position', short: 'Logo on the left or centred in the header.' },
  'design.header_style': { title: 'Header background', short: 'Light or primary-colour header.' },
  'design.header_sticky': { title: 'Sticky header', short: 'Keeps the header at the top while scrolling.' },
  'design.footer_style': { title: 'Footer background', short: 'Light, dark or primary-colour footer.' },
  'design.gallery': {
    title: 'Product page image',
    short: 'Product image on the left or right on wide screens.',
  },
  'design.blog_layout': { title: 'Blog page', short: 'Blog posts as a grid or a list.' },
  'design.input_style': { title: 'Form fields', short: 'Outlined or filled form fields.' },
  'design.live_preview': {
    title: 'Live preview',
    short: 'How the changes look on a sample storefront page before saving.',
  },
  'design.device': { title: 'Device', short: 'Shows the preview at phone, tablet or desktop width.' },
  'design.contrast': {
    title: 'Readability check',
    short: 'Checks WCAG AA contrast (at least 4.5:1) between text and backgrounds.',
  },
  'design.versions': {
    title: 'Version history',
    short: 'Each published theme, its date and who published it.',
  },
  'design.rollback': {
    title: 'Go back to this version',
    short: 'Publishes the selected version again and loads it into the draft.',
  },
  'design.note': { title: 'Version note', short: 'Why this was published; shown in the version history.' },
  'page.menus': {
    title: 'Menus',
    short: 'Edit the storefront header menu (mega menu) and footer menu.',
    whatItDoes:
      'Header: up to 3 levels (item, column heading, link). Footer: 2 levels (column heading, links). Category and product links are stored by id, so renamed addresses keep working; hidden targets drop out automatically.',
  },
  'menus.location': { title: 'Menu location', short: 'Header or footer menu; each is saved separately.' },
  'menus.add': { title: 'Add item', short: 'Adds a new top-level item at the end.' },
  'menus.add_child': { title: 'Add sub-item', short: 'Adds an item below this one.' },
  'menus.label': { title: 'Name shown in the menu', short: 'The text visitors see (max 60 characters).' },
  'menus.link': {
    title: 'Link',
    short:
      'Where the item goes: category, product, all products, a page, an address or a heading without link.',
  },
  'menus.category': { title: 'Category', short: 'The category to link to. Hidden categories are not shown.' },
  'menus.product': { title: 'Product', short: 'Search by name, SKU or barcode and pick one.' },
  'menus.url': { title: 'Address', short: 'A site path (e.g. /iletisim) or a full https:// address.' },
  'menus.new_tab': { title: 'Open in a new tab', short: 'For links to other sites.' },
  'menus.move': { title: 'Reorder', short: 'Moves the item up or down within its level.' },
  'menus.remove': {
    title: 'Remove',
    short: 'Removes the item and its sub-items (the storefront changes when you save).',
  },
  'menus.save': { title: 'Save menu', short: 'Saves the menu for the selected location.' },
  'menus.page': {
    title: 'Page',
    short: 'A company or campaign page; shown in the menu only while it is live.',
  },
  'menus.unavailable': {
    title: 'Target not visible',
    short: 'The category is hidden, or the product or page is not live; the item is not shown.',
  },
  'page.pages': {
    title: 'Pages',
    short: 'Build the home page layout, company pages and campaign pages section by section.',
    whatItDoes:
      'Pages are made of sections: hero, text, image + text, product grid, category tiles and FAQ. Save a draft, preview it on the storefront with a signed link, then publish. Each publish is kept as an immutable version. Without a published home layout the storefront shows the default home page.',
  },
  'page.page_editor': {
    title: 'Page editor',
    short: 'Edit the page title, address, SEO details and sections.',
    whatItDoes:
      'Add, reorder, hide or remove sections. Saving a draft does not change the storefront. Publishing creates a new version, optionally with start and end dates. Health content or health claims need Veterinary Expert approval before publishing.',
  },
  'pages.create': { title: 'New page', short: 'Creates a draft page and opens the editor.' },
  'pages.kind': {
    title: 'Page type',
    short: 'Home layout (one per store, at "/"), company page or campaign page (at /sayfa/short-name).',
  },
  'pages.title': {
    title: 'Page title',
    short: 'Shown in the panel and on the storefront (max 120 characters).',
  },
  'pages.slug': {
    title: 'Short name (address)',
    short:
      'Lowercase letters, digits and hyphens. On a live page it changes on save and the old address redirects (needs publish permission).',
  },
  'pages.seo_title': { title: 'SEO title', short: 'Shown in search results and the browser tab (max 70).' },
  'pages.seo_description': {
    title: 'SEO description',
    short: 'Short text under the title in search results (max 170).',
  },
  'pages.health_content': {
    title: 'Health content',
    short: 'Pages about pet health need Veterinary Expert approval before publishing.',
  },
  'pages.preview': {
    title: 'Preview',
    short: 'Saves changes and opens the draft on the storefront in a new tab (signed link, valid 2 hours).',
  },
  'pages.save': { title: 'Save draft', short: 'Saves changes as a draft; the storefront does not change.' },
  'pages.publish': {
    title: 'Publish',
    short: 'Publishes the draft as a new version, optionally from a start date until an end date.',
  },
  'pages.schedule': {
    title: 'Publish and end dates',
    short: 'Leave empty to publish now with no end. The end date must be after the start date.',
  },
  'pages.unpublish': {
    title: 'Unpublish',
    short: 'Removes the page from the storefront; drafts and versions are kept.',
  },
  'pages.delete': { title: 'Delete page', short: 'Permanently deletes a page that was never published.' },
  'pages.review': {
    title: 'Expert review',
    short:
      'The Veterinary Expert approves or rejects with a reason. Approval is bound to the current content.',
  },
  'pages.review_note': {
    title: 'Review note',
    short: 'Required when rejecting: tell the editor what to fix.',
  },
  'pages.section_add': {
    title: 'Add section',
    short: 'Adds a section of the chosen type at the end (max 40).',
  },
  'pages.section_hide': {
    title: 'Hide section',
    short: 'Keeps the section in the draft but hides it on the storefront.',
  },
  'pages.section_move': { title: 'Move section', short: 'Moves the section up or down.' },
  'pages.section_remove': { title: 'Remove section', short: 'Removes the section from the draft.' },
  'pages.heading': {
    title: 'Section heading',
    short: 'Shown above the section. The first hero heading is the page heading.',
  },
  'pages.cta': {
    title: 'Button',
    short: 'Text and address of the hero button (e.g. /urunler or an https:// address).',
  },
  'pages.rich_text': {
    title: 'Text editor',
    short:
      'Paragraphs, headings, bold, italic, lists, quotes, rules, links and images (alt text required). Move a block by dragging or with the up/down buttons.',
  },
  'pages.image': { title: 'Upload image', short: 'Uploads a PNG, JPEG, WebP, AVIF or GIF image.' },
  'pages.image_alt': { title: 'Alt text', short: 'Briefly describe what the image shows. Required.' },
  'pages.image_position': { title: 'Image position', short: 'Image left or right of the text on desktop.' },
  'pages.grid_source': {
    title: 'Product source',
    short: 'Newest products, a category, or products you pick.',
  },
  'pages.grid_limit': { title: 'Number of products', short: 'How many products to show (2–24).' },
  'pages.products': {
    title: 'Selected products',
    short: 'Search and add products (max 24); shown in this order.',
  },
  'pages.tiles': {
    title: 'Category tiles',
    short: 'Categories shown as tiles. Hidden categories are not shown.',
  },
  'pages.faq': { title: 'Question and answer', short: 'Each FAQ entry; shown as an expandable list.' },
  'page.banners': {
    title: 'Banners',
    short: 'Manage the home slider, promo tiles, category banner, announcement bar and popup.',
  },
  'page.banner_editor': {
    title: 'Banner editor',
    short: 'Placement, text, images, audience and dates of a banner.',
  },
  'banners.create': { title: 'New banner', short: 'Creates a draft banner; not shown until activated.' },
  'banners.placement': {
    title: 'Placement',
    short: 'Home slider, home secondary area, category top, announcement bar or popup.',
  },
  'banners.name': { title: 'Internal name', short: 'Only shown in the panel.' },
  'banners.title': { title: 'Title', short: 'Large text on the banner (max 120).' },
  'banners.text': { title: 'Text', short: 'Short text (max 300). Required for the announcement bar.' },
  'banners.cta': { title: 'Button text', short: 'Without button text, the whole banner is the link.' },
  'banners.href': { title: 'Link address', short: 'A site path (e.g. /urunler) or an https:// address.' },
  'banners.desktop_image': {
    title: 'Desktop image',
    short: 'Required for slider, secondary area and category top.',
  },
  'banners.mobile_image': { title: 'Mobile image', short: 'Optional; the desktop image is used if missing.' },
  'banners.image_alt': { title: 'Alt text', short: 'Describe the image. Required when there is an image.' },
  'banners.audience': { title: 'Audience', short: 'Everyone, non-dealers, or approved dealers only.' },
  'banners.dates': { title: 'Start and end', short: 'Empty means from activation until stopped.' },
  'banners.dismiss_days': {
    title: 'Hide after closing',
    short: 'Days the popup stays hidden after a visitor closes it.',
  },
  'banners.categories': { title: 'Categories', short: 'Where the category banner appears; none means all.' },
  'banners.save': {
    title: 'Save',
    short: 'Saves the banner; changes to an active banner go live immediately.',
  },
  'banners.activate': { title: 'Activate', short: 'Shows the banner between its start and end dates.' },
  'banners.deactivate': { title: 'Stop', short: 'Removes the banner from the storefront immediately.' },
  'banners.move': {
    title: 'Reorder',
    short: 'Moves the banner within its placement; the slider follows this order.',
  },
  'banners.delete': { title: 'Delete banner', short: 'Permanently deletes an inactive banner.' },
  'pages.post_list': {
    title: 'Blog posts',
    short: 'Shows the newest live posts of the chosen category (1–12).',
  },
  'pages.banners': { title: 'Banner area', short: 'Shows the active banners of the chosen area.' },
  'products.media_alt': {
    title: 'Image alt text',
    short: 'Describe the image; empty means the product name is used.',
  },
  'products.media_order': {
    title: 'Image order',
    short: 'The first image is the cover; the gallery follows this order.',
  },
  'page.blog': { title: 'Blog', short: 'Manage articles, guides and blog categories.' },
  'page.post_editor': {
    title: 'Post editor',
    short: 'Title, text, cover, references, related products and SEO of a post.',
  },
  'blog.create': { title: 'New post', short: 'Creates a draft post and opens the editor.' },
  'blog.filter': { title: 'Filter', short: 'All, awaiting expert approval, live or drafts.' },
  'page.content_calendar': {
    title: 'Content calendar',
    short: 'Planned and past publishing events for the month: posts, pages and banners, day by day.',
  },
  'content_calendar.month': {
    title: 'Month selection',
    short:
      'Go to the previous or next month; "This month" returns to today. Month boundaries use Turkey time.',
  },
  'content_calendar.kind': { title: 'Type', short: 'Limit the calendar to blog posts, pages or banners.' },
  'page.content_report': {
    title: 'Content report',
    short:
      'Post, page and banner statuses; publishing by category and month; posts not updated for a long time; Search Console clicks.',
  },
  'content_report.states': {
    title: 'Status summary',
    short: 'Live: on the storefront. Planned: scheduled. Draft: never published. Expired: end date passed.',
  },
  'content_report.stale': {
    title: 'Posts not updated',
    short:
      'Live posts whose last published version is older than 365 days, oldest first. Review and publish again.',
  },
  'content_report.search_console': {
    title: 'Search Console clicks',
    short:
      'Last 28 days from the linked Search Console property: clicks, impressions, CTR and average position per post. Storefront language only.',
  },
  'page.forms': {
    title: 'Forms',
    short: 'Edit the contact form fields and the extra fields of the dealer application.',
  },
  'forms.kind': {
    title: 'Form',
    short:
      'Contact form: all fields are editable. Dealer application: company and tax fields are fixed; add extra fields only.',
  },
  'forms.enabled': {
    title: 'Show the form on the Contact page',
    short:
      'When on, the form appears on the storefront Contact page. It cannot be turned on before the privacy notice is published.',
  },
  'forms.notify': {
    title: 'Email notice for new messages',
    short:
      'Owners and admins get an email without the message content, with a link to the panel; at most once per 15 minutes.',
  },
  'forms.fields': {
    title: 'Form fields',
    short:
      'Label, type, help text and required flag per field; reorder with the arrows. The contact form email field is required and cannot be removed.',
  },
  'forms.add_field': {
    title: 'Add field',
    short: 'Adds an empty short text field at the end (max 20 fields).',
  },
  'forms.save': { title: 'Save form', short: 'Saves fields and settings; goes live immediately.' },
  'forms.label': {
    title: 'Label',
    short: 'Field name shown to visitors (max 80). Left empty on a preset field, the default label is used.',
  },
  'forms.type': {
    title: 'Field type',
    short: 'Input is checked by type: email, phone, number, date; a choice list accepts only its options.',
  },
  'forms.help_text': { title: 'Help text', short: 'Short note shown under the field (max 200).' },
  'forms.options': { title: 'Options', short: 'One option per line (2–30, all different).' },
  'forms.required': {
    title: 'Required field',
    short: 'Visitors must fill it in; a checkbox must be ticked.',
  },
  'page.contact_messages': {
    title: 'Contact messages',
    short: 'Messages sent through the storefront contact form.',
  },
  'messages.filter': { title: 'Show', short: 'New (unhandled) messages, handled ones, or all.' },
  'messages.handle': {
    title: 'Mark as handled',
    short: 'Records that you replied; undo with "Mark as new".',
  },
  'messages.delete': {
    title: 'Delete message',
    short: 'Deletes the message permanently (cannot be undone).',
  },
  'topbar.store': {
    title: 'Working store',
    short:
      'Panel screens select this store by default; changing the store on a screen changes it too. Remembered in this browser.',
  },
  'topbar.jobs': {
    title: 'Background jobs',
    short:
      'Progress of long jobs (imports, bulk product actions, site crawl, page speed). Running jobs and those finished in the last 10 minutes.',
  },
  'system.observability': {
    title: 'Observability (OpenTelemetry)',
    short:
      'Request and background job durations, error rates and traces are sent to a collector (Grafana Cloud, Honeycomb, New Relic or your own OpenTelemetry Collector). No personal data: only method, route template, status code and job name. Turn on in Integrations → Observability.',
  },
  'system.csp_reports': {
    title: 'Storefront content security violations',
    short:
      "When the storefront's security policy blocks a script, image, frame or connection that is not allowed, the visitor's browser reports it. A newly added tag or video that does not work, or an attempt to inject code into a page, shows up here. Only a summary is kept (rule, blocked domain, page path, count, date); no personal data, IP addresses or browser-extension reports. Entries not seen for 30 days are deleted.",
  },
  'system.offsite': {
    title: 'Off-site backup copy',
    short:
      'The nightly backup is encrypted and uploaded to object storage (S3 / R2). Turn on in Integrations → Object storage.',
  },
  'page.marketing_audience': {
    title: 'Subscribers and consent',
    short: 'Newsletter subscribers, suppression list and İYS consent records.',
  },
  'audience.subscribers': {
    title: 'Newsletter subscribers',
    short: 'Subscribed through the storefront form (double opt-in).',
  },
  'audience.status': { title: 'Status', short: 'Subscribed, awaiting confirmation, unsubscribed or all.' },
  'audience.search': {
    title: 'Search email',
    short: 'Searches by part of the address (needs personal data permission).',
  },
  'audience.subscriber_actions': {
    title: 'End subscription / Delete',
    short:
      'Ending adds the address to the suppression list and reports a refusal to İYS. Delete is for privacy requests.',
  },
  'audience.suppressions': {
    title: 'Suppression list',
    short: 'No marketing message is sent to these addresses.',
  },
  'audience.channel': { title: 'Channel', short: 'Email, SMS or WhatsApp.' },
  'audience.address': { title: 'Address', short: 'Email address or phone number.' },
  'audience.note': { title: 'Note', short: 'Why it was suppressed.' },
  'audience.suppress': {
    title: 'Suppress',
    short: 'Adds the address to the list and reports a refusal to İYS.',
  },
  'audience.channel_filter': { title: 'Channel', short: 'Filters the list by channel.' },
  'audience.unsuppress': { title: 'Remove', short: 'Removes the block; consent must be given again.' },
  'audience.iys': {
    title: 'İYS records',
    short: 'Consents and refusals must reach İYS within three business days.',
  },
  'audience.iys_filter': { title: 'Show', short: 'Records to report, sent, reported manually or all.' },
  'audience.iys_download': {
    title: 'Download file (CSV)',
    short: 'Records to report, with İYS field names.',
  },
  'audience.iys_mark': { title: 'Mark as reported', short: 'Marks records you uploaded in the İYS panel.' },
  'page.segments': {
    title: 'Segments',
    short: 'RFM groups and rule-based customer segments.',
  },
  'page.segment_editor': { title: 'Segment', short: 'Name, rules, preview and members of a segment.' },
  'segments.rfm': {
    title: 'RFM groups',
    short: 'Recency, frequency and spend scored 1–5; groups follow from scores.',
  },
  'segments.recompute': {
    title: 'Recalculate now',
    short: 'Updates RFM scores and segment counts (runs nightly).',
  },
  'segments.save_rfm': {
    title: 'Save as segment',
    short: 'Saves this RFM group as a segment for campaigns.',
  },
  'segments.list': {
    title: 'Segments',
    short: 'Counts are from the last count (on save, recalculation, nightly).',
  },
  'segments.name': { title: 'Name', short: 'Shown when choosing an audience; unique.' },
  'segments.description': { title: 'Description', short: 'What the segment is for.' },
  'segments.match': { title: 'Match', short: 'All conditions, or any one of them.' },
  'segments.conditions': {
    title: 'Conditions',
    short: 'Up to 10. Spend is net of refunds and cancellations.',
  },
  'segments.preview': {
    title: 'Preview',
    short: 'Shows the match count and first 10 customers without saving.',
  },
  'segments.save': { title: 'Save', short: 'Saves the segment and updates its count.' },
  'segments.delete': { title: 'Delete', short: 'Deletes the segment; customers are not affected.' },
  'segments.members': {
    title: 'Customers in segment',
    short: 'Customers matching now; emails masked without PII permission.',
  },
  'customers.tags': {
    title: 'Tags',
    short: 'Labels used as filters and in segment rules (max 30); included in KVKK data copies.',
  },
  'customers.save_tags': { title: 'Save tags', short: 'Saves the tags.' },
  'customers.tag_filter': { title: 'Tag', short: 'Filters the list by tag.' },
  'customers.summary': {
    title: 'Customer summary',
    short: 'Live from orders: spend to date and last 12 months, orders, average basket, order interval, RFM.',
  },
  'customers.notes': { title: 'Internal notes', short: 'Team notes; included in KVKK data copies.' },
  'customers.save_notes': { title: 'Save note', short: 'Saves the note (audited).' },
  'customers.timeline': {
    title: 'Activity',
    short: 'Orders, returns, reviews, questions, consents, messages and KVKK requests in one list.',
  },
  'email_routing.transactional': {
    title: 'Transactional email',
    short: 'Orders, shipping, passwords and invitations.',
  },
  'email_routing.marketing': {
    title: 'Marketing email',
    short: 'Newsletters and campaigns; a separate stream protects reputation.',
  },
  'email_routing.save': { title: 'Save sending choice', short: 'Takes effect immediately.' },
  'email_routing.webhooks': {
    title: 'Notification URLs',
    short: 'Where the provider sends bounce, complaint and unsubscribe events.',
  },
  'email_routing.rotate': { title: 'Regenerate URLs', short: 'Old URLs stop working immediately.' },
  'page.email_templates': {
    title: 'Email templates',
    short: 'Block-based templates for newsletters and campaigns.',
  },
  'page.email_template_editor': {
    title: 'Email template',
    short: 'Add and order blocks; preview and send a test to yourself.',
  },
  'email_templates.create': {
    title: 'New template',
    short: 'Opens an empty template in the chosen store and language.',
  },
  'email_templates.save': { title: 'Save', short: 'Saves the template; health claims are re-checked.' },
  'email_templates.name': { title: 'Template name', short: 'Shown in the panel only.' },
  'email_templates.subject': { title: 'Subject', short: 'Inbox subject, up to 200 characters.' },
  'email_templates.preheader': {
    title: 'Preheader',
    short: 'Short text shown next to the subject in the inbox.',
  },
  'email_templates.merge_fields': {
    title: 'Personalisation fields',
    short: 'Text after the vertical bar is used when there is no value.',
  },
  'email_templates.blocks': {
    title: 'Content blocks',
    short: 'Up to 40 blocks; incomplete blocks block saving.',
  },
  'email_templates.move': { title: 'Reorder', short: 'Drag by the handle or use the arrow buttons.' },
  'email_templates.add_block': { title: 'Add block', short: 'Adds the block to the end of the template.' },
  'email_templates.delete': { title: 'Delete', short: 'Deletes the template; other languages stay.' },
  'email_templates.align': { title: 'Alignment', short: 'Left or centre.' },
  'email_templates.heading': { title: 'Heading', short: 'Up to 200 characters.' },
  'email_templates.level': { title: 'Size', short: 'Large heading or subheading.' },
  'email_templates.text': { title: 'Text', short: 'Plain text; blank line for paragraphs, **bold**.' },
  'email_templates.image': { title: 'Image', short: 'PNG, JPEG, WebP or GIF; 1200 px wide is enough.' },
  'email_templates.image_alt': {
    title: 'Image description',
    short: 'Shown when images are off; required for linked images.',
  },
  'email_templates.link': { title: 'Link', short: 'A store path (/campaign) or a full http(s) address.' },
  'email_templates.width': { title: 'Width', short: 'Full width or centred half width.' },
  'email_templates.button_label': { title: 'Button text', short: 'Short text saying what happens.' },
  'email_templates.products_source': {
    title: 'Product source',
    short: 'Newest, a collection, a category or hand-picked.',
  },
  'email_templates.columns': { title: 'Columns', short: 'Cards side by side on desktop.' },
  'email_templates.limit': { title: 'Number of products', short: '1–12 products from the source.' },
  'email_templates.collection': { title: 'Collection', short: 'Products matching the collection, by name.' },
  'email_templates.category': { title: 'Category', short: 'Products in the category, by name.' },
  'email_templates.products_pick': {
    title: 'Products',
    short: 'Search by name, SKU or barcode; order is kept.',
  },
  'email_templates.show_price': { title: 'Show price', short: 'Lowest tax-inclusive price at send time.' },
  'email_templates.spacer': { title: 'Spacer', short: 'Adds vertical space.' },
  'email_templates.cart': { title: 'Cart block', short: 'Filled in the abandoned cart automation.' },
  'email_templates.order': { title: 'Order block', short: 'Filled in post-purchase automations.' },
  'email_templates.quote': {
    title: 'Quote block',
    short: 'Filled in the dealer quote follow-up automation.',
  },
  'email_templates.preview': {
    title: 'Preview',
    short: 'Updates as you type; personal fields are placeholders.',
  },
  'email_templates.device': {
    title: 'Screen and format',
    short: 'Desktop, phone and the plain-text version.',
  },
  'email_templates.test_send': { title: 'Send test', short: 'Sends the saved version to panel users only.' },
  'email_templates.test_to': { title: 'Recipients', short: 'Panel users’ emails, up to 5.' },
  'page.campaigns': {
    title: 'Email campaigns',
    short: 'Send a template to an audience; follow progress and results.',
  },
  'page.campaign_editor': {
    title: 'Email campaign',
    short: 'Template, subject, audience, A/B test, speed; sending and report.',
  },
  'campaigns.create': { title: 'New campaign', short: 'Opens a draft campaign for the chosen store.' },
  'campaigns.save': { title: 'Save', short: 'Saves the draft; no email is sent.' },
  'campaigns.name': { title: 'Campaign name', short: 'Used in reports and in the campaign code of links.' },
  'campaigns.template': {
    title: 'Email template',
    short: 'Main template; other language versions go to those recipients.',
  },
  'campaigns.subject': {
    title: 'Subject',
    short: 'Overrides the template subject for main-language recipients.',
  },
  'campaigns.preheader': { title: 'Preheader', short: 'Overrides the template preheader.' },
  'campaigns.audience': { title: 'Audience', short: 'Segments are evaluated when sending starts.' },
  'campaigns.subscribers': { title: 'Newsletter subscribers', short: 'Confirmed subscribers of this store.' },
  'campaigns.estimate': {
    title: 'Count recipients',
    short: 'Shows how many can receive it and why others are excluded.',
  },
  'campaigns.ab': { title: 'A/B test', short: 'The subject with the higher click rate goes to the rest.' },
  'campaigns.subject_b': { title: 'Subject B', short: 'Subject A is the campaign (or template) subject.' },
  'campaigns.ab_percent': { title: 'Test group', short: 'Share of recipients in the test (10–50%).' },
  'campaigns.ab_wait': { title: 'Waiting time', short: 'Time before the winner is chosen.' },
  'campaigns.rate': { title: 'Sending speed', short: 'Emails per minute (10–3000).' },
  'campaigns.delete': { title: 'Delete', short: 'Only drafts can be deleted.' },
  'campaigns.send': {
    title: 'Send',
    short: 'Shows the recipient count; sends now or later after confirmation.',
  },
  'campaigns.scheduled_at': { title: 'Send time', short: 'Up to 90 days ahead.' },
  'campaigns.status': { title: 'Sending status', short: 'Refreshes every 5 seconds.' },
  'campaigns.resume': { title: 'Resume', short: 'Continues sending to unsent recipients.' },
  'campaigns.cancel': { title: 'Stop sending', short: 'Unsent recipients will not get the email.' },
  'campaigns.report': {
    title: 'Report',
    short: 'Clicks are counted through a tracking address; opens are not measured.',
  },
  'page.automations': {
    title: 'Marketing automations',
    short: 'Email flows triggered by events, and sending rules.',
  },
  'page.automation_editor': {
    title: 'Automation flow',
    short: 'Turn on/off; trigger delay, steps, templates; report.',
  },
  'automations.settings': { title: 'Sending rules', short: 'Frequency cap and quiet hours for all flows.' },
  'automations.frequency_cap': {
    title: 'Frequency cap',
    short: 'Skips the email if the person got another marketing email within this time.',
  },
  'automations.quiet_hours': { title: 'Quiet hours', short: 'No emails in this window (Türkiye time).' },
  'automations.settings_save': { title: 'Save rules', short: 'Applies from the next send.' },
  'automations.save': { title: 'Save', short: 'Saves the flow; turning it on asks for confirmation.' },
  'automations.enabled': {
    title: 'Flow on',
    short: 'Needs a template for every email step and email set up.',
  },
  'automations.trigger': { title: 'Trigger', short: 'The event that puts a person into the flow.' },
  'automations.store': { title: 'Store', short: 'Brand, links and templates come from this store.' },
  'automations.after_hours': {
    title: 'How long after',
    short: 'Delay after the cart change or order (1–72 h).',
  },
  'automations.days': {
    title: 'Days',
    short: 'Days after delivery, the last order or the quote being sent (1–365).',
  },
  'automations.steps': {
    title: 'Steps',
    short: 'Up to 10 steps; at least one email; cannot end with a wait.',
  },
  'automations.step_tools': { title: 'Move or delete step', short: 'Takes effect after Save.' },
  'automations.add_step': { title: 'Add step', short: 'Adds a wait, condition or email step at the end.' },
  'automations.reset': { title: 'Back to defaults', short: 'Restores the preset steps (not saved yet).' },
  'automations.report': { title: 'Report', short: 'Orders are counted by the flow campaign code in links.' },
  'page.product_alerts': {
    title: 'Stock and price alerts',
    short: '"Notify me" requests and price drop emails for favorites.',
  },
  'product_alerts.settings': { title: 'Settings', short: 'Both off by default; email sending required.' },
  'product_alerts.stock_toggle': {
    title: '"Notify me" on the storefront',
    short: 'Shows the email form on out-of-stock items.',
  },
  'product_alerts.price_drop': {
    title: 'Price drop notice',
    short: 'Daily email when a favorite’s price drops enough.',
  },
  'product_alerts.store': { title: 'Store', short: 'Prices, cards and links come from this store.' },
  'product_alerts.min_percent': { title: 'Minimum drop', short: 'Compared with the lowest price seen.' },
  'product_alerts.save': { title: 'Save', short: 'Turning a notice on asks for confirmation.' },
  'product_alerts.requests': {
    title: 'Requests',
    short: 'Waiting requests show interest in sold-out items.',
  },
  'product_alerts.view': { title: 'Requests', short: 'Lists the variant’s requests in order.' },
  'product_alerts.cancel': { title: 'Cancel', short: 'Closes the request; no email is sent.' },
  'page.message_campaigns': {
    title: 'SMS and WhatsApp',
    short: 'SMS and WhatsApp campaigns, setup status and sending settings.',
  },
  'page.message_campaign_editor': {
    title: 'SMS/WhatsApp campaign',
    short: 'Channel, template, audience, estimated cost; sending, progress and report.',
  },
  'page.sms_templates': {
    title: 'SMS templates',
    short: 'SMS texts used in campaigns and their expert approval.',
  },
  'page.sms_template_editor': {
    title: 'SMS template',
    short: 'Text, personal fields, live length count and expert approval.',
  },
  'messaging.create': { title: 'New SMS/WhatsApp campaign', short: 'Creates a draft; saving sends nothing.' },
  'messaging.setup': {
    title: 'Setup status',
    short: 'A channel with something missing shows "Setup required".',
  },
  'messaging.settings': {
    title: 'Settings',
    short: 'Apply to all SMS/WhatsApp campaigns; needs two-step verification.',
  },
  'messaging.sms_provider': { title: 'SMS provider', short: 'Connect and test it in Integrations first.' },
  'messaging.opt_out': {
    title: 'Opt-out text',
    short: 'Required in commercial SMS: how to opt out in the same channel.',
  },
  'messaging.unit_cost_sms': { title: 'Cost per SMS part', short: 'Only used for the cost estimate.' },
  'messaging.unit_cost_whatsapp': {
    title: 'Cost per WhatsApp message',
    short: 'Only used for the cost estimate.',
  },
  'messaging.frequency_cap': {
    title: 'Frequency cap',
    short: 'No second campaign message to a number within this time.',
  },
  'messaging.staging_phones': {
    title: 'Staging test numbers',
    short: 'On staging, only these numbers get messages.',
  },
  'messaging.settings_save': {
    title: 'Save settings',
    short: 'Also applies to scheduled and running sends.',
  },
  'messaging.save': { title: 'Save', short: 'Saves the draft; sends nothing.' },
  'messaging.channel': { title: 'Channel', short: 'SMS uses your template; WhatsApp a Meta-approved one.' },
  'messaging.name': { title: 'Campaign name', short: 'Shown in the panel, not to recipients.' },
  'messaging.sms_template': {
    title: 'SMS template',
    short: 'Templates with health claims need expert approval.',
  },
  'messaging.whatsapp_template': {
    title: 'WhatsApp template',
    short: 'Approved templates from Meta; re-checked at send time.',
  },
  'messaging.whatsapp_params': {
    title: 'Template variables',
    short: 'Values for {{1}}, {{2}}… in the template.',
  },
  'messaging.segments': {
    title: 'Audience',
    short: 'Segments are recalculated at send time; one message per number.',
  },
  'messaging.estimate': {
    title: 'Count recipients and cost',
    short: 'Recipients, exclusions, SMS parts and cost.',
  },
  'messaging.delete': { title: 'Delete', short: 'Deletes a draft or a stopped campaign that never started.' },
  'messaging.send': { title: 'Send', short: 'Shows recipients and cost, then sends now or schedules.' },
  'messaging.scheduled_at': { title: 'Send time', short: 'Up to 90 days ahead; Turkey time.' },
  'messaging.status': { title: 'Sending status', short: 'Refreshes every 5 seconds; explains pauses.' },
  'messaging.resume': { title: 'Resume', short: 'Continues with recipients not yet sent to.' },
  'messaging.cancel': { title: 'Stop', short: 'Cancels recipients not yet sent to.' },
  'messaging.report': { title: 'Report', short: 'Sent, delivered, failed and skipped; reasons and cost.' },
  'sms_templates.create': { title: 'New SMS template', short: 'Opens an empty template.' },
  'sms_templates.save': { title: 'Save', short: 'Changing the text requires expert approval again.' },
  'sms_templates.name': { title: 'Template name', short: 'Shown when choosing a template.' },
  'sms_templates.body': {
    title: 'Text',
    short: 'Keep it short; the MERSİS number and opt-out text are added.',
  },
  'sms_templates.fields': { title: 'Personal fields', short: 'Inserts a field at the cursor.' },
  'sms_templates.preview': { title: 'Preview', short: 'Text to be sent, character set and parts.' },
  'sms_templates.delete': { title: 'Delete', short: 'Deletes it unless a pending campaign uses it.' },
  'page.push': {
    title: 'Push notifications',
    short: 'Browser notification campaigns, storefront button and setup.',
  },
  'page.push_editor': {
    title: 'Notification campaign',
    short: 'Title, text, page, audience; preview, sending and report.',
  },
  'push.create': { title: 'New notification campaign', short: 'Creates a draft; sends nothing.' },
  'push.setup': { title: 'Setup', short: 'Missing items show "Setup required" or "Missing".' },
  'push.create_key': {
    title: 'Create key',
    short: 'Generates a key pair on the server; the private key stays encrypted.',
  },
  'push.replace_key': {
    title: 'Replace key',
    short: 'All subscriptions end; visitors must turn notifications on again.',
  },
  'push.contact': { title: 'Contact address', short: 'mailto: or https:; required by push services.' },
  'push.enabled': {
    title: 'Storefront button',
    short: 'Permission is asked only when the visitor presses it. Off by default.',
  },
  'push.settings_save': { title: 'Save', short: 'Turning on needs a key and a contact address.' },
  'push.save': { title: 'Save', short: 'Saves the draft; sends nothing.' },
  'push.name': { title: 'Campaign name', short: 'Shown in the panel, not to recipients.' },
  'push.title': { title: 'Title', short: 'Keep it short; browsers may cut after ~50 characters.' },
  'push.body': { title: 'Text', short: 'Max 180 characters; health claims need expert approval.' },
  'push.url': { title: 'Page to open', short: 'A store path starting with /; UTM is added.' },
  'push.audience': { title: 'Audience', short: 'All subscribers, or signed-in members in segments.' },
  'push.estimate': { title: 'Count browsers', short: 'How many subscriptions get it; sends nothing.' },
  'push.preview': { title: 'Preview', short: 'Approximate look.' },
  'push.delete': { title: 'Delete', short: 'Deletes a draft or a stopped campaign that never started.' },
  'push.send': { title: 'Send', short: 'Shows the count, then sends now or schedules.' },
  'push.scheduled_at': { title: 'Send time', short: 'Up to 90 days ahead.' },
  'push.status': { title: 'Sending status', short: 'Refreshes every 5 seconds; explains pauses.' },
  'push.resume': { title: 'Resume', short: 'Continues with browsers not yet sent to.' },
  'push.cancel': { title: 'Stop', short: 'Cancels browsers not yet sent to.' },
  'push.report': { title: 'Report', short: 'Accepted sends, expired subscriptions, reasons and orders.' },
  'notifications.section': {
    title: 'Mobile notifications',
    short: 'Your devices and which events notify you.',
  },
  'notifications.events': { title: 'Events that notify me', short: 'Only events you have permission for.' },
  'notifications.quiet_hours': { title: 'Quiet hours', short: 'No notifications in this range.' },
  'notifications.save': { title: 'Save preferences', short: 'Affects only your notifications.' },
  'notifications.test': { title: 'Test notification', short: 'Sends a test to your registered devices.' },
  'notifications.device_remove': {
    title: 'Remove device',
    short: 'No more notifications are sent to this device. Affects only your own devices.',
  },
  'page.promotions': {
    title: 'Automatic promotions',
    short: 'Promotions without a coupon: product discount, cart tiers, buy X pay Y, free shipping.',
  },
  'page.promotion_editor': {
    title: 'Automatic promotion',
    short: 'Type, rule, target products, audience, priority, combining and dates; text customers see.',
  },
  'promotions.create': {
    title: 'New promotion',
    short: 'Opens a draft; drafts are not visible to customers.',
  },
  'promotions.save': { title: 'Save', short: 'Saves a draft or paused promotion.' },
  'promotions.activate': {
    title: 'Make live',
    short: 'Starts the promotion; asks for two-step verification.',
  },
  'promotions.pause': { title: 'Pause', short: 'Stops applying it to carts immediately.' },
  'promotions.delete': { title: 'Delete draft', short: 'Only a draft that never went live can be deleted.' },
  'promotions.name': { title: 'Promotion name', short: 'Shown in the panel and reports only.' },
  'promotions.label': { title: 'Name shown to customers', short: 'Cart, checkout and product page; max 80.' },
  'promotions.kind': {
    title: 'Promotion type',
    short: 'Product discount, cart tiers, buy X pay Y or free shipping.',
  },
  'promotions.percent': { title: 'Discount percent', short: 'E.g. 20 or 7.5; rounded down.' },
  'promotions.buy': { title: 'Buy', short: 'Items per group (2–20).' },
  'promotions.pay': { title: 'Pay', short: 'Paid items per group; the cheapest are free.' },
  'promotions.min_subtotal': {
    title: 'Minimum cart amount',
    short: 'After product discounts; empty = always.',
  },
  'promotions.basis': { title: 'Threshold type', short: 'Cart amount or eligible item count.' },
  'promotions.tier_type': { title: 'Discount type', short: 'Percent or fixed amount.' },
  'promotions.tiers': { title: 'Tiers', short: 'Up to 5; the highest eligible tier applies.' },
  'promotions.add_tier': { title: 'Add tier', short: 'Adds a threshold and discount row.' },
  'promotions.remove_tier': { title: 'Remove tier', short: 'Removes this tier from the draft.' },
  'promotions.currency': { title: 'Currency', short: 'Applies only to carts in this currency.' },
  'promotions.targets': { title: 'Target products', short: 'None selected = all products.' },
  'promotions.categories': { title: 'Categories', short: 'Subcategories are included.' },
  'promotions.brands': { title: 'Brands', short: 'Products of the selected brands.' },
  'promotions.collections': { title: 'Collections', short: 'Current products of the collection.' },
  'promotions.products': { title: 'Products', short: 'Search by name, SKU or barcode.' },
  'promotions.audience': {
    title: 'Audience',
    short: 'Everyone, retail or dealers; optional groups and segments.',
  },
  'pricing.list_market': { title: 'Market', short: 'If set, the list applies only in that market.' },
  'products.compliance': {
    title: 'Country eligibility',
    short: 'Per-country sales status, registration/permit number and verification.',
  },
  'products.compliance_status': { title: 'Sales status', short: 'A reason is required when not for sale.' },
  'products.compliance_reason': { title: 'Reason', short: 'Why it cannot be sold (internal).' },
  'products.compliance_registration': {
    title: 'Registration / permit number',
    short: 'Shown on the product page once verified.',
  },
  'products.compliance_verification': {
    title: 'Verification',
    short: 'Whether the number was checked against the official source.',
  },
  'page.fx': {
    title: 'Exchange rates',
    short: 'CBRT daily indicative rates, fetch status and rate-derived price lists.',
  },
  'fx.refresh': {
    title: 'Update rates now',
    short: 'Fetches the latest CBRT bulletin now (at most once a minute).',
  },
  'fx.rates': { title: 'Rates', short: 'Forex and banknote buying/selling rates of the latest bulletin.' },
  'fx.derived': {
    title: 'Rate-derived lists',
    short: 'Lists whose prices are calculated from a base list by rate.',
  },
  'fx.recompute': { title: 'Recalculate now', short: 'Recalculates the list with the latest rate.' },
  'pricing.fx': {
    title: 'Derive from rate',
    short: 'Calculates the list prices from a base list in another currency with the CBRT rate.',
  },
  'pricing.fx_base': { title: 'Base list', short: 'A manually priced list in another currency.' },
  'pricing.fx_rate_type': { title: 'Rate type', short: 'Forex/banknote buying or selling rate.' },
  'pricing.fx_markup': { title: 'Markup', short: 'Percentage added after conversion; negative lowers.' },
  'pricing.fx_ending': { title: 'Price ending', short: 'Rounds up to a whole number, .90 or .99.' },
  'pricing.fx_auto': {
    title: 'Update automatically',
    short: 'Recalculates on each new bulletin and when base prices change.',
  },
  'pricing.fx_clear': {
    title: 'Switch to manual prices',
    short: 'Removes the rate link; current prices stay.',
  },
  'orders.fx': { title: 'Exchange rate', short: 'CBRT forex buying rate at order time; never changes.' },
  'payments.card_currencies': {
    title: 'Card payments in foreign currency',
    short: 'Cards are offered only in ticked currencies the provider supports.',
  },
  'shipping.currency': {
    title: 'Fee currency',
    short: 'The method is offered only on orders in this currency.',
  },
  'page.dealer_reports': {
    title: 'Dealer reports',
    short: 'Dealer sales, products, categories, aging and collections by dealer, sales rep or region.',
  },
  'dealer_reports.report': { title: 'Report', short: 'Sales, products, categories, aging or collections.' },
  'dealer_reports.period': { title: 'Period', short: 'From and to days inclusive; at most one year.' },
  'dealer_reports.group_by': { title: 'Group by', short: 'Totals by dealer, sales rep or region.' },
  'dealer_reports.rep': { title: 'Sales rep', short: 'Only this sales rep’s orders or dealers.' },
  'dealer_reports.region': { title: 'Region', short: 'Only dealers in this region.' },
  'page.international_report': {
    title: 'Sales by country',
    short:
      'Orders, exports, gross, VAT, shipping, refunds, net and TRY equivalent by shipping country and currency.',
  },
  'international_report.period': {
    title: 'Period',
    short: 'Order day (Türkiye time), both days inclusive; at most one year.',
  },
  'international_report.store': { title: 'Store', short: 'Leave empty for all stores you can access.' },
  'international_report.csv': { title: 'Download CSV', short: 'Downloads the report with the same filters.' },
  'international_report.try_total': {
    title: 'TRY equivalent',
    short: 'Net amounts converted with the CBRT rate recorded on each order.',
  },
  'dealer_reports.csv': { title: 'Download CSV', short: 'Downloads the report with the same filters.' },
  'promotions.companies': {
    title: 'Dealer companies',
    short: 'Dealer-only campaign: applies only to users of the selected approved dealer companies.',
  },
  'promotions.groups': { title: 'Customer groups', short: 'Guests count as members of retail groups.' },
  'promotions.segments': { title: 'Segments', short: 'Signed-in members of the selected segments.' },
  'promotions.priority': { title: 'Priority', short: 'Higher is tried first (−100 to 100).' },
  'promotions.stacking': { title: 'Combining', short: 'Exclusive or combinable with other promotions.' },
  'promotions.allow_coupon': {
    title: 'With a coupon',
    short: 'If off, coupons are refused while it applies.',
  },
  'promotions.starts_at': { title: 'Start', short: 'Empty = when made live.' },
  'promotions.ends_at': { title: 'End', short: 'Empty = no end; shown on product pages.' },
  'promotions.preview': { title: 'What customers see', short: 'Name, condition and end date.' },
  'promotions.excluded': {
    title: 'Products without discount',
    short: 'Price raised within 10 days before the start.',
  },
  'promotions.usage': { title: 'Usage', short: 'Orders, total discount and order total.' },
  'page.gift_cards': {
    title: 'Gift cards',
    short: 'Prepaid cards spent with a code at checkout: create, balance, status and expiry.',
  },
  'page.gift_card_detail': {
    title: 'Gift card',
    short: 'Card balance, transactions (issue, order use, refund, cancel, adjustment) and settings.',
  },
  'gift_cards.create': {
    title: 'Create gift card',
    short: 'Creates a card with an amount and optional expiry. The code is shown only once; you pass it on.',
  },
  'gift_cards.search': {
    title: 'Last four characters',
    short: 'Searches by the last four characters; full codes are not stored.',
  },
  'gift_cards.state_filter': {
    title: 'Status',
    short: 'Usable, disabled, expired or no balance.',
  },
  'gift_cards.amount': { title: 'Amount', short: 'Initial balance of the card (at least 1.00).' },
  'gift_cards.currency': {
    title: 'Currency',
    short: 'The card works only in stores selling in this currency.',
  },
  'gift_cards.expires_at': {
    title: 'Expiry date',
    short: 'Valid until the end of the day; empty means no expiry. Check with your lawyer first.',
  },
  'gift_cards.note': { title: 'Internal note', short: 'Visible in the panel only.' },
  'gift_cards.copy_code': {
    title: 'Copy code',
    short: 'Copies the code. It is not shown again after the window closes.',
  },
  'gift_cards.adjust': {
    title: 'Adjust balance',
    short: 'Adds to or deducts from the balance with a reason. The balance cannot go negative.',
  },
  'gift_cards.toggle': {
    title: 'Disable / re-enable card',
    short: 'A disabled card is refused at checkout; earlier orders are not affected.',
  },
  'gift_cards.save': { title: 'Save', short: 'Saves the expiry date and internal note.' },
  'tenders.adjust_direction': { title: 'Action', short: 'Add balance or deduct from it.' },
  'tenders.adjust_amount': { title: 'Amount', short: 'Positive amount; choose the direction in "Action".' },
  'tenders.adjust_note': { title: 'Reason', short: 'Required; recorded in history and audit log.' },
  'customers.store_credit_adjust': {
    title: 'Add / deduct credit',
    short: 'Adds (e.g. goodwill) or deducts store credit with a reason. The customer sees the amount only.',
  },
  'customers.store_credit_currency': {
    title: 'Currency',
    short: 'Credit is kept per currency and used only in orders of the same currency.',
  },
  'customers.gift_card_issue': {
    title: 'Issue gift card',
    short: 'Creates a gift card recorded for this customer. The code is shown once.',
  },
  'orders.refund_payment': {
    title: 'Payment to refund',
    short: 'With several payments (e.g. gift card + card), choose which one to refund.',
  },
  'orders.refund_store_credit': {
    title: 'Refund as store credit',
    short: 'Adds the amount to store credit instead of money; only with consent, never for withdrawals.',
  },
  'orders.refund_consent': {
    title: 'Customer consent',
    short: 'Confirm the customer accepted store credit; required.',
  },
  'page.loyalty': {
    title: 'Loyalty program',
    short: 'Earning and redemption rates, waiting and validity periods, VIP tiers and program summary.',
  },
  'loyalty.enabled': {
    title: 'Loyalty program on',
    short: 'When on, members earn/use points and tier benefits apply.',
  },
  'loyalty.currency': {
    title: 'Currency',
    short: 'Points are earned and used only in orders of this currency.',
  },
  'loyalty.earn': { title: 'Points per 1 unit', short: 'e.g. 10: 10 points for every 1 TRY spent.' },
  'loyalty.value': { title: 'Points for 1 unit', short: 'e.g. 100: 100 points = 1 TRY.' },
  'loyalty.min_redeem': {
    title: 'Minimum points to use',
    short: 'Minimum balance needed to use points at checkout.',
  },
  'loyalty.hold_days': {
    title: 'Wait after delivery',
    short: 'Points are added this many days after delivery.',
  },
  'loyalty.expiry': {
    title: 'Point validity',
    short: 'Earned points expire after this many months; 0 = never.',
  },
  'loyalty.tiers': {
    title: 'VIP tiers',
    short: 'Tiers by net spending in the last 12 months; discount and free shipping.',
  },
  'loyalty.tier_name': { title: 'Tier name', short: 'Visible to customers.' },
  'loyalty.tier_spend': {
    title: 'Minimum spend (12 months)',
    short: 'Members at or above this spend get the tier.',
  },
  'loyalty.tier_discount': {
    title: 'Tier discount',
    short: 'Percentage off products (max 50%), after promotions.',
  },
  'loyalty.tier_shipping': { title: 'Free shipping', short: 'No shipping fee for this tier.' },
  'loyalty.tier_add': { title: 'Add tier', short: 'Adds a tier row; applies when saved.' },
  'loyalty.tier_remove': { title: 'Remove tier', short: 'Removes the tier row; applies when saved.' },
  'loyalty.save': { title: 'Save', short: 'Saves the settings (two-step verification).' },
  'loyalty.adjust_points': { title: 'Points', short: 'Points to add or deduct (positive whole number).' },
  'customers.loyalty_adjust': {
    title: 'Add / deduct points',
    short: 'Adds or deducts loyalty points with a reason; the customer does not see the reason.',
  },
  'page.referrals': {
    title: 'Referral program',
    short:
      'Invite a friend: first-order discount, referrer reward coupon, waiting period, limit and recent invites.',
  },
  'referrals.enabled': {
    title: 'Referral program on',
    short: 'When on, members can get invite codes and codes work in the cart.',
  },
  'referrals.currency': {
    title: 'Currency',
    short: 'Invite codes and reward coupons work only in carts of this currency.',
  },
  'referrals.friend': {
    title: 'Friend discount',
    short: 'Coupon discount for a friend placing a first order with an invite code.',
  },
  'referrals.referrer': {
    title: 'Referrer reward',
    short: 'Single-use coupon given to the referrer after delivery and the waiting period.',
  },
  'referrals.reward_type': { title: 'Discount type', short: 'Percentage (max 50%) or fixed amount.' },
  'referrals.reward_value': {
    title: 'Discount',
    short: 'Percentage (e.g. 10) or fixed amount (e.g. 50.00).',
  },
  'referrals.reward_min': {
    title: 'Minimum cart total',
    short: 'Minimum cart total for the code. Empty: no minimum.',
  },
  'referrals.hold_days': {
    title: 'Wait after delivery',
    short: 'The reward is issued this many days after the friend’s order is delivered.',
  },
  'referrals.valid_days': {
    title: 'Reward coupon validity',
    short: 'Days the reward coupon stays valid (7–365).',
  },
  'referrals.max_rewards': {
    title: 'Maximum rewards per member',
    short: 'Most rewards one member can earn.',
  },
  'referrals.save': {
    title: 'Save',
    short: 'Saves the settings and updates all invite codes (two-step verification).',
  },
  'page.affiliates': {
    title: 'Affiliates',
    short: 'Affiliates and influencers, coupon or link matching, commissions and manual payout.',
  },
  'affiliates.create': { title: 'Add affiliate', short: 'Creates a new affiliate or influencer.' },
  'affiliates.edit': { title: 'Edit', short: 'Changes rate, coupon, source or status for future orders.' },
  'affiliates.name': { title: 'Name', short: 'Name of the partner or channel; shown in the CSV.' },
  'affiliates.kind': { title: 'Type', short: 'Affiliate or influencer; for grouping only.' },
  'affiliates.email': {
    title: 'Email',
    short: 'Internal contact record; the system does not email partners.',
  },
  'affiliates.source': {
    title: 'Link source (utm_source)',
    short: 'Value added to the partner’s links; lowercase letters, digits, dash, underscore.',
  },
  'affiliates.discount': {
    title: 'Coupon',
    short: 'Partner coupon; orders with it are attributed before links.',
  },
  'affiliates.rate': {
    title: 'Commission rate',
    short: 'Percentage of the product amount excluding VAT and shipping (max 50%).',
  },
  'affiliates.status': { title: 'Status', short: 'Paused partners get no new orders.' },
  'affiliates.note': { title: 'Internal note', short: 'Internal notes; not visible to the partner.' },
  'affiliates.save': { title: 'Save', short: 'Saves the affiliate (two-step verification).' },
  'commissions.filter_affiliate': {
    title: 'Affiliate filter',
    short: 'Limits the list and CSV to one affiliate.',
  },
  'commissions.filter_status': {
    title: 'Status filter',
    short: 'Without a status, the CSV contains approved commissions.',
  },
  'commissions.export': { title: 'Download CSV', short: 'Excel-compatible CSV for payouts.' },
  'commissions.select': { title: 'Selection', short: 'Only approved commissions can be selected.' },
  'commissions.mark_paid': {
    title: 'Mark as paid',
    short: 'Marks selected approved commissions as paid; cannot be undone.',
  },
  'commissions.paid_note': {
    title: 'Payment note',
    short: 'Receipt number, date, etc. (at least 3 characters).',
  },
  'page.support_tickets': {
    title: 'Support requests',
    short:
      'Customer requests: status, assignment, priority; saved replies; support, satisfaction and return reasons report.',
  },
  'page.support_ticket': {
    title: 'Support request',
    short:
      'Conversation, reply to customer, internal note, saved reply; status, priority, topic and assignee.',
  },
  'support.filter_status': {
    title: 'Status filter',
    short: '"Open and waiting" shows requests awaiting a reply or the customer.',
  },
  'support.filter_assigned': {
    title: 'Assignee filter',
    short: 'Requests assigned to you, unassigned or to a staff member.',
  },
  'support.filter_priority': { title: 'Priority filter', short: 'Only requests with the selected priority.' },
  'support.search': {
    title: 'Search',
    short: 'Request number, full customer email or a word from the subject.',
  },
  'support.report_period': { title: 'Report period', short: 'Last 7, 30, 90 or 365 days.' },
  'support.macro_add': { title: 'Add saved reply', short: 'Saves a frequent reply with a name.' },
  'support.macro_edit': { title: 'Edit', short: 'Changes the name or text of the saved reply.' },
  'support.macro_delete': {
    title: 'Delete',
    short: 'Deletes the saved reply; sent replies are not affected.',
  },
  'support.macro_title': { title: 'Name', short: 'Short unique name (2–80 characters).' },
  'support.macro_body': {
    title: 'Reply text',
    short: '{{name}} is replaced with the customer name when inserted.',
  },
  'support.status': {
    title: 'Status',
    short: 'Awaiting reply, awaiting customer, resolved (reopens on reply) or closed.',
  },
  'support.priority': {
    title: 'Priority',
    short: 'Urgent and high requests come first; customers do not see it.',
  },
  'support.category': { title: 'Topic', short: 'Request topic; counted in the report.' },
  'support.assignee': { title: 'Assignee', short: 'Only active staff who can reply to requests.' },
  'support.notify_resolved': { title: 'Resolved email', short: 'Emails the customer with a rating link.' },
  'support.save_details': { title: 'Save changes', short: 'Saves status, priority, topic and assignee.' },
  'support.insert_macro': {
    title: 'Insert saved reply',
    short: 'Inserts the saved reply into the text box.',
  },
  'support.reply_body': { title: 'Reply or internal note', short: 'Up to 5000 characters.' },
  'support.send_reply': {
    title: 'Send reply',
    short: 'Shown on the request page and emailed to the customer.',
  },
  'support.send_resolve': {
    title: 'Send and resolve',
    short: 'Sends the reply and marks the request resolved.',
  },
  'support.add_note': { title: 'Add internal note', short: 'Visible to the team only.' },
  'support.convert_subject': {
    title: 'Request subject',
    short: 'Shown in the list and in the customer email.',
  },
  'messages.convert': {
    title: 'Convert to request',
    short: 'Turns the message into a support request; the message is marked handled.',
  },
  'page.live_chat': {
    title: 'Live chat',
    short:
      'Live support chats from the storefront: availability, waiting/ongoing chats, reply, close, convert and settings.',
  },
  'chat.available': {
    title: 'Available for chat',
    short: 'Visitors can start chats while this is on and the page is open.',
  },
  'chat.claim': { title: 'Take chat', short: 'Assigns the waiting chat to you.' },
  'chat.reply': { title: 'Reply', short: 'Up to 2000 characters.' },
  'chat.send': { title: 'Send', short: 'Sends the message to the visitor.' },
  'chat.close': { title: 'Close chat', short: 'Ends the chat; the visitor can start a new one.' },
  'chat.convert': {
    title: 'Convert to request',
    short: 'Moves the chat to a support request (email required).',
  },
  'chat.enabled': {
    title: 'Live support window',
    short: 'Shows the live support button on the storefront; off by default.',
  },
  'chat.retention': { title: 'Retention', short: 'Closed chats are deleted after this many days.' },
  'chat.idle': { title: 'Inactivity', short: 'Chats without messages for this many minutes close.' },
  'chat.save': { title: 'Save settings', short: 'Saves the chat settings.' },
  'page.google_ads_campaigns': {
    title: 'Google Ads campaigns',
    short:
      'Prepares Performance Max and Shopping campaigns from your products, gets them approved and applies them through the Google Ads API.',
  },
  'page.google_ads_campaign': {
    title: 'Google Ads campaign',
    short: 'The campaign’s values, products and status in Google; change requests and history.',
  },
  'gads.settings': {
    title: 'Budget cap and settings',
    short: 'Monthly ad budget cap and automatic stop at the cap.',
  },
  'gads.new_campaign': {
    title: 'New campaign',
    short: 'Wizard: store, type, products, budget and bidding, locations/languages/dates, summary.',
  },
  'gads.stop_all': {
    title: 'Stop all Google campaigns',
    short: 'Emergency: pauses all live campaigns and cancels pending spend requests.',
  },
  'gads.month': {
    title: 'This month',
    short: 'Spend this month, cap, estimated monthly maximum and API operations today.',
  },
  'gads.campaigns': {
    title: 'Campaigns',
    short: 'All Google Ads campaigns: status, budget, monthly maximum, spend, alerts.',
  },
  'gads.approvals': {
    title: 'Awaiting approval',
    short: 'Requests that can spend money and their money impact.',
  },
  'gads.decide': {
    title: 'Approve / Reject',
    short: 'Approval needs two-step verification; only the owner can approve their own request.',
  },
  'gads.decision_note': { title: 'Note', short: 'Reason for the decision; written to the audit log.' },
  'gads.monthly_cap': {
    title: 'Monthly ad budget cap',
    short: 'Monthly upper limit for all Google Ads campaigns.',
  },
  'gads.auto_stop': {
    title: 'Automatic stop',
    short:
      'Off by default. When this month’s spend reaches the cap, live campaigns are paused without approval.',
  },
  'gads.daily_limit': {
    title: 'Daily API operation limit',
    short: 'With Explorer access, Google limits daily operations on real accounts.',
  },
  'gads.submit': {
    title: 'Save draft / Send for approval',
    short: 'A draft is not sent to Google; once approved, the campaign is created paused in Google.',
  },
  'gads.store': {
    title: 'Store',
    short: 'The campaign’s Google Ads and Merchant Center accounts come from the store’s Google connections.',
  },
  'gads.kind': {
    title: 'Campaign type',
    short: 'Performance Max runs across all Google channels; Shopping only in Google Shopping.',
  },
  'gads.name': { title: 'Campaign name', short: 'Name shown in Google Ads; must be unique in the account.' },
  'gads.daily_budget': {
    title: 'Daily budget',
    short: 'Google may spend up to 2× in a day; monthly maximum ≈ daily × 30.4.',
  },
  'gads.bidding': {
    title: 'Bid strategy',
    short: 'Performance Max: conversion value or conversions; Shopping: conversion value or clicks.',
  },
  'gads.target_roas': {
    title: 'Target ROAS',
    short: 'Target conversion value per unit spent (percent). Lowering it can increase spend.',
  },
  'gads.target_cpa': {
    title: 'Target cost per conversion',
    short: 'Target average cost per purchase. Raising it can increase spend.',
  },
  'gads.request_note': { title: 'Note', short: 'Reason for the approver; written to the audit log.' },
  'gads.products': {
    title: 'Products',
    short: 'Only products that enter the Google feed and are not in another campaign are added.',
  },
  'gads.countries': {
    title: 'Countries',
    short: 'Countries where ads are shown. Adding a country needs approval; removing does not.',
  },
  'gads.languages': {
    title: 'Languages',
    short: 'Targeted user languages. Adding a language needs approval.',
  },
  'gads.dates': {
    title: 'Dates',
    short: 'Start and end (in the account’s time zone). Extending or removing the end needs approval.',
  },
  'gads.label': {
    title: 'Product label',
    short: 'Campaign label sent to Merchant as custom_label_0; the product filter includes this label.',
  },
  'gads.accept_google': {
    title: 'Accept Google’s values',
    short: 'Aligns the system record with Google Ads; nothing is sent to Google.',
  },
  'gads.operations': {
    title: 'Campaign actions',
    short: 'Actions needing approval go to approval; others are queued directly after the checkbox.',
  },
  'gads.product_states': {
    title: 'Product status',
    short:
      'Queued → Sent to Google — processing → Eligible / Limited / Not eligible (with Google’s reasons).',
  },
  'gads.operation_history': {
    title: 'Changes',
    short: 'Who requested, who approved, whether it was applied to Google; the reason if it failed.',
  },
  'gads.recommendations': {
    title: 'Google recommendations',
    short: 'Read from Google on request; applying always needs approval.',
  },
  'gads.change_history': {
    title: 'Change history',
    short: 'Google Ads change log for the last 14 days; not stored.',
  },
  'gads.send_operation': {
    title: 'Send',
    short:
      'Approval actions go to approval, others to the queue; the worker validates first, then applies and reads back.',
  },
  'gads.product_box': {
    title: 'Google ads',
    short: 'The product’s Google Ads campaign, its status in Google and eligibility.',
  },
  'gads.bulk': {
    title: 'Add to / remove from Google campaign',
    short: 'Adds selected products to a campaign or removes them; ineligible ones are listed with reasons.',
  },
  'gads.filter': { title: 'Google ads filter', short: 'Filters products by their campaign status.' },
  'gads.search_source': {
    title: 'Draft source',
    short: 'The product or category the ad draft is built from.',
  },
  'gads.search_suggest': {
    title: 'Draft from data',
    short: 'Suggests headlines, descriptions, display path and keywords; you edit and send for approval.',
  },
  'gads.search_final_url': {
    title: 'Final URL',
    short: 'The page people open after clicking the ad; must be on the store’s own domain.',
  },
  'gads.search_path': {
    title: 'Display path',
    short: 'Two parts shown after the domain in the ad (up to 15 characters each).',
  },
  'gads.search_headlines': {
    title: 'Headlines',
    short: '3–15 headlines, up to 30 characters each; Google tries the combinations.',
  },
  'gads.search_descriptions': {
    title: 'Descriptions',
    short: '2–4 descriptions, up to 90 characters each.',
  },
  'gads.search_keywords': {
    title: 'Keywords',
    short: 'Which searches the ad can show for, with match type (broad, phrase, exact).',
  },
  'gads.search_negatives': {
    title: 'Negative keywords',
    short: 'Searches the ad should not show for (for example “free”).',
  },
  'gads.search_ideas': {
    title: 'Suggest keywords',
    short: 'Ideas and monthly searches from Google’s planning service.',
  },
  'gads.text_review': {
    title: 'Expert review',
    short: 'If the ad text has health-claim findings, the Veterinary Expert Reviewer must approve it.',
  },
  'gads.text_review_decide': {
    title: 'Approve / reject text',
    short: 'Expert decision; rejection needs a reason.',
  },
  'gads.search_edit_ad': {
    title: 'Edit ad text',
    short: 'Saved directly on a draft; on a live campaign a text change is an approval request.',
  },
  'gads.search_edit_keywords': {
    title: 'Edit keywords',
    short: 'Adding needs approval; removing only is applied right away after the checkbox.',
  },
  'gads.search_edit_negatives': {
    title: 'Edit negatives',
    short: 'Adding is applied right away after the checkbox; removing needs approval.',
  },
  'gads.search_google_state': {
    title: 'Status in Google',
    short: 'Google’s policy approval, review status and ad strength (read hourly).',
  },
  'gads.access_level': {
    title: 'Google Ads API access level',
    short: 'The level Google granted your Cloud project: Explorer, Basic or Standard.',
  },
  'page.needs_finder': {
    title: 'Needs finder',
    short:
      "Edits the My Pet's Needs question set, gets it approved by the expert and publishes it on the storefront.",
  },
  'needs_finder.generate': {
    title: 'First draft from product data',
    short: 'Creates support areas from categories, rules from products and a general emergency question.',
  },
  'needs_finder.start_empty': {
    title: 'Empty draft',
    short: 'Starts without areas, questions or rules; you add everything.',
  },
  'needs_finder.save': {
    title: 'Save draft',
    short: 'Saves your edits; if the content changed, the expert approval lapses.',
  },
  'needs_finder.status': {
    title: 'Status and publishing',
    short: 'Review status, the live version and the checklist.',
  },
  'needs_finder.problems': {
    title: 'Checklist',
    short: 'Items that block publishing (red) and items to watch (amber).',
  },
  'needs_finder.submit': {
    title: 'Submit for review',
    short: 'Sends the saved draft to the veterinary expert reviewer for approval.',
  },
  'needs_finder.review': {
    title: 'Expert decision',
    short: 'The veterinary expert reviewer approves the draft or rejects it with a reason.',
  },
  'needs_finder.review_note': {
    title: 'Expert note',
    short: 'Reason for the decision; required when rejecting, written to the audit log.',
  },
  'needs_finder.findings': {
    title: 'Health claim findings',
    short:
      'Expressions such as preventing or treating disease in the texts; not a final verdict, the expert decides.',
  },
  'needs_finder.products': {
    title: 'Products that may be suggested',
    short: 'All products in rules and alternatives, with reasons and storefront status.',
  },
  'needs_finder.publish': {
    title: 'Publish',
    short: 'Publishes the expert-approved draft on the storefront at /ihtiyac-bulucu.',
  },
  'needs_finder.unpublish': {
    title: 'Unpublish',
    short: 'Closes the storefront page; the draft and approval are kept.',
  },
  'needs_finder.regenerate': {
    title: 'Regenerate draft',
    short: 'Replaces the current draft with a new one from product data; edits and approval are lost.',
  },
  'needs_finder.pet_profiles': {
    title: 'Save my pet',
    short: "Members can save their pet's basics to their account with explicit consent. Off by default.",
  },
  'needs_finder.translations': {
    title: 'Other languages',
    short: 'English, German and Arabic versions of the text; if empty, Turkish is shown.',
  },
  'needs_finder.condition': {
    title: 'Condition',
    short:
      'When a rule, question or exclusion applies: species, age, weight, neutering, pregnancy, area, answer.',
  },
  'needs_finder.row_actions': {
    title: 'Reorder, edit, delete',
    short: 'Changes the order, edits or removes the item from the draft; can be undone until saved.',
  },
  'needs_finder.areas': {
    title: 'Support areas',
    short: 'Topics the visitor chooses; shown only for the ticked species.',
  },
  'needs_finder.area_label': { title: 'Area name', short: 'Name shown on the storefront (e.g. "Joints").' },
  'needs_finder.area_description': {
    title: 'Short description',
    short: 'One sentence shown under the area; optional.',
  },
  'needs_finder.area_species': {
    title: 'Shown for species',
    short: 'The area is listed only when one of these species is chosen.',
  },
  'needs_finder.area_categories': {
    title: 'Related categories',
    short: 'Which product categories the area comes from (for generation and tracking).',
  },
  'needs_finder.id': { title: 'ID', short: 'Short name used in conditions; do not change after saving.' },
  'needs_finder.questions': {
    title: 'Questions',
    short: 'Questions asked in order; they branch by area or by earlier answers.',
  },
  'needs_finder.question_text': {
    title: 'Question text',
    short: 'The sentence asked to the visitor. Do not write dosage or diagnosis questions.',
  },
  'needs_finder.question_help': {
    title: 'Help text',
    short: 'Short explanation under the question; optional.',
  },
  'needs_finder.question_area': {
    title: 'Linked area',
    short: 'If set, the question is asked only when this area is chosen; "General" asks everyone.',
  },
  'needs_finder.question_multiple': {
    title: 'Multiple choice',
    short: 'The visitor can tick more than one option.',
  },
  'needs_finder.options': { title: 'Options', short: 'The answers to the question (2–12).' },
  'needs_finder.option_label': { title: 'Option text', short: 'Shown as an answer on the storefront.' },
  'needs_finder.option_emergency': {
    title: 'Emergency option',
    short: 'If chosen, no products are suggested; "Please contact a veterinarian first" is shown.',
  },
  'needs_finder.option_exclusive': {
    title: 'Chosen on its own',
    short: 'Like "None" in a multiple-choice question: choosing it clears the others.',
  },
  'needs_finder.rules': {
    title: 'Recommendation rules',
    short: 'If the condition matches, the product is suggested with its approved reason; sorted by priority.',
  },
  'needs_finder.rule_product': {
    title: 'Suggested product',
    short: 'The product suggested when the condition matches.',
  },
  'needs_finder.rule_reason': {
    title: 'Why it is suggested',
    short: 'Approved reason shown under the product. Do not claim to prevent or treat disease.',
  },
  'needs_finder.rule_priority': {
    title: 'Priority',
    short: 'Higher-priority suggestions come first (0–100).',
  },
  'needs_finder.rule_alternatives': {
    title: 'Alternatives',
    short: 'Approved alternatives tried in order when the product is out of stock (up to 5).',
  },
  'needs_finder.exclusions': {
    title: 'Exclusions',
    short:
      'If the condition matches, the listed products are not suggested by any rule; always takes precedence.',
  },
  'needs_finder.exclusion_products': {
    title: 'Products not to suggest',
    short: 'Products no rule may suggest when the condition matches.',
  },
  'needs_finder.exclusion_note': {
    title: 'Note',
    short: 'Reason for the exclusion (in the panel and preview).',
  },
  'needs_finder.texts': { title: 'Texts', short: 'The page intro, emergency text and bundle promotion.' },
  'needs_finder.intro': {
    title: 'Intro text',
    short: 'Shown at the top of the page; if empty, the default text.',
  },
  'needs_finder.emergency_text': {
    title: 'Emergency text',
    short: 'Shown under "Please contact a veterinarian first" when an emergency is chosen.',
  },
  'needs_finder.bundle': {
    title: 'Needs bundle promotion',
    short:
      'Optional automatic promotion (off by default); if running, its name and condition appear in the result.',
  },
  'needs_finder.preview': {
    title: 'Preview',
    short: 'Same calculation as the storefront rule engine; stock is ignored.',
  },
  'needs_finder.report': { title: 'Report', short: 'Totals only; no visitor identity or answers.' },
  'needs_finder.report_days': { title: 'Period', short: 'Last 7, 30 or 90 days (Istanbul time).' },
  'page_designs.needs_finder': {
    title: "My Pet's Needs design",
    short: 'Look of the question flow: cards, chat or single page + live summary.',
  },
  'page.ads': {
    title: 'Ad center',
    short:
      'Shows ad spend together with the store’s real orders: campaign performance, ROAS, cost per order, profit, alerts and approved budget changes.',
  },
  'ads.connections': {
    title: 'Ad connections',
    short: 'Meta and TikTok are connected on the Connections page (read only).',
  },
  'ads.sync_now': {
    title: 'Sync now',
    short: 'Reads the last 7 days (30 on first sync) from live Meta/TikTok connections.',
  },
  'ads.add_account': { title: 'Add account', short: 'Adds an ad account whose report you upload as a file.' },
  'ads.save_account': {
    title: 'Save',
    short: 'Saves the account. Archived accounts are hidden from the report.',
  },
  'ads.account_platform': {
    title: 'Platform',
    short: 'Where the ads run; sets the default matching sources.',
  },
  'ads.account_name': { title: 'Account name', short: 'Name shown in the report (2–80 characters).' },
  'ads.account_external_id': {
    title: 'Account ID on the platform',
    short: 'Optional; must be unique per platform.',
  },
  'ads.account_currency': {
    title: 'Currency',
    short: 'The ad account currency; uploaded amounts must use it.',
  },
  'ads.account_utm_sources': {
    title: 'Matching sources (utm_source)',
    short: 'Orders whose last visit source is one of these are attributed to this account.',
  },
  'ads.account_status': {
    title: 'Status',
    short: 'Archived accounts are left out of the report; data is kept.',
  },
  'ads.account_actions': {
    title: 'Upload report and edit',
    short: 'Upload a platform report or edit the account.',
  },
  'ads.upload_file': {
    title: 'Report file',
    short: 'CSV or XLSX up to 10 MB with date, campaign and spend columns.',
  },
  'ads.apply_import': {
    title: 'Save data',
    short: 'Saves the previewed valid rows; existing rows are overwritten.',
  },
  'ads.report': {
    title: 'Campaign performance',
    short: 'CTR, CPC, CPM, cost per order and ROAS from real orders.',
  },
  'ads.period': { title: 'Period', short: 'Last 7, 30 or 90 days (Istanbul time).' },
  'ads.platform_filter': { title: 'Platform', short: 'Limits the report to one platform.' },
  'ads.compare': { title: 'Compare', short: 'Select up to 4 campaigns to compare side by side.' },
  'ads.anomalies': {
    title: 'Alerts',
    short: 'Spend spikes, CTR drops, spend without clicks and stale syncs.',
  },
  'ads.product_ads': {
    title: 'Shopping ad products',
    short:
      'Spend, clicks and conversions per product from Google Shopping ads, with net sales and product ROAS.',
  },
  'ads.products': { title: 'Product performance', short: 'Products in orders attributed to ads.' },
  'ads.request_change': { title: 'Request change', short: 'Sends a budget or delivery change for approval.' },
  'ads.change_kind': { title: 'Change', short: 'Daily budget or delivery status.' },
  'ads.change_budget': { title: 'New daily budget', short: 'In the account currency.' },
  'ads.change_status': { title: 'New status', short: 'Pause or resume the campaign.' },
  'ads.change_note': { title: 'Reason', short: 'Why the change is needed (3–500 characters).' },
  'ads.send_request': {
    title: 'Send for approval',
    short: 'Saves the request; nothing changes on the platform.',
  },
  'ads.history': { title: 'Change history', short: 'Requests, approvals, uploads and syncs.' },
  'ads.change_actions': {
    title: 'Decide, applied, cancel',
    short: 'Approve/reject, mark applied or withdraw a request.',
  },
  'ads.approve': { title: 'Approve', short: 'Approves the request (two-step verification required).' },
  'ads.reject': { title: 'Reject', short: 'Rejects the request.' },
  'ads.decision_note': { title: 'Decision note', short: 'Optional note shown in the history.' },
  'payments.go_live_skip_sandbox': {
    title: 'Go live without a test payment',
    short:
      'Owner only. Allows going live without a successful sandbox payment; requires two-step verification and is audited.',
  },
  'payments.go_live_confirm': {
    title: 'Go live',
    short:
      'Switches card payments to live; real cards will be charged. Requires a passing live connection test.',
  },
  'payments.first_live_order': {
    title: 'First live order check',
    short:
      'Place a small order with your own card, check it is paid and refund it. This notice disappears after the refund.',
  },
  'page.retention': {
    title: 'Retention and destruction',
    short:
      'Automatic deletion of personal data whose retention period has ended (off by default), fixed periods and the destruction log.',
  },
  'page.sales_regions': {
    title: 'Sales regions',
    short: 'Regions made of provinces/districts and their managers; assign dealers to regions by location.',
  },
  'sales_regions.new': {
    title: 'Add region',
    short: 'New sales region with name, places and an optional manager.',
  },
  'sales_regions.auto_assign': {
    title: 'Assign by province/district',
    short:
      'Matches dealers’ province/district to regions: preview first, then apply. By default only dealers without a region.',
  },
  'sales_regions.manager': {
    title: 'Region manager',
    short:
      'Sees all dealers in the region (with "assigned dealers only" scope). Must be an active panel user who can see dealers.',
  },
  'sales_regions.places': {
    title: 'Provinces and districts',
    short: 'Leave the district empty to include the whole province; choose a district to include only it.',
  },
  'dealers.sales_assignment': {
    title: 'Sales rep and region',
    short:
      'The dealer’s rep and region. Scoped reps and region managers see this dealer; orders record the rep at order time.',
  },
  'dealers.sales_rep': {
    title: 'Sales rep',
    short: 'An active panel user who can see dealers. Empty: no rep.',
  },
  'dealers.sales_region': {
    title: 'Sales region',
    short: 'Chosen here or assigned in bulk by province/district on the Sales regions page.',
  },
  'dealers.sales_filter': {
    title: 'Rep and region filter',
    short: 'Dealers of a rep or region; "No rep" / "No region" shows unassigned dealers.',
  },
  'page.bizimhesap': {
    title: 'BizimHesap',
    short:
      'BizimHesap is the master record of dealer accounts: cash accounts, dealer ↔ account matching, write permissions, correction of invoices sent without a cash account and the collection queue.',
  },
  'dealers.bizimhesap_code': {
    title: 'BizimHesap account',
    short: 'The dealer’s BizimHesap account code; invoices are not sent while unlinked.',
  },
  'dealers.bizimhesap_link': { title: 'Account code', short: 'Checked against the BizimHesap account list.' },
  'bizimhesap.statement': {
    title: 'BizimHesap statement',
    short: 'Account movements and balance in BizimHesap.',
  },
  'bizimhesap.refresh_one': {
    title: 'Update this dealer',
    short: 'Fetches this dealer’s statement now (read only).',
  },
  'bizimhesap.refresh_all': { title: 'Update now', short: 'Queues the statements of all linked dealers.' },
  'bizimhesap.diff': {
    title: 'Difference report',
    short: 'BizimHesap balance + not transferred vs. this system’s ledger.',
  },
  'invoices.provider_cancel': {
    title: 'BizimHesap cancellation',
    short:
      'Cancels the BizimHesap invoice record of a fully cancelled order; e-documents need a return invoice.',
  },
  'invoices.provider_cancel_resolve': {
    title: 'Cancellation result',
    short: 'What you saw in the BizimHesap panel.',
  },
  'bizimhesap.cash_map': {
    title: 'Cash accounts',
    short: 'A BizimHesap cash account per payment method. Paid orders are invoiced with it.',
  },
  'bizimhesap.refresh_cashiers': {
    title: 'Fetch cash accounts',
    short: 'Fetches the cash accounts from BizimHesap.',
  },
  'bizimhesap.cash_card': { title: 'Card payment account', short: 'Orders paid via iyzico/PayTR.' },
  'bizimhesap.cash_bank_transfer': { title: 'Bank transfer account', short: 'Orders paid by bank transfer.' },
  'bizimhesap.cash_cash_on_delivery': {
    title: 'Cash on delivery account',
    short: 'Orders collected at the door.',
  },
  'bizimhesap.cash_manual': {
    title: 'Manual payment account',
    short: 'Payments marked in the panel with "Payment received".',
  },
  'bizimhesap.cash_cash': {
    title: 'Cash collection account',
    short: 'Cash collections entered for dealer accounts.',
  },
  'bizimhesap.save_cash': {
    title: 'Save cash accounts',
    short: 'Saves the selection; recorded in the audit log.',
  },
  'bizimhesap.links': {
    title: 'Dealer ↔ BizimHesap account',
    short: 'Dealer invoices use this account code. Invoices of unlinked dealers are not sent.',
  },
  'bizimhesap.import_customers': {
    title: 'Import accounts',
    short: 'Matches the BizimHesap account list with dealers (dealer code, then tax number). Preview first.',
  },
  'bizimhesap.ambiguous': {
    title: 'Ambiguous match',
    short: 'Several candidates; choose the right account or leave unlinked.',
  },
  'bizimhesap.writes': {
    title: 'Writing to BizimHesap',
    short:
      'Steps that write to your real accounting account. Off by default; owner/admin and confirmation required.',
  },
  'bizimhesap.write_collections': {
    title: 'Send collections',
    short: 'Queued collections are sent with "Send".',
  },
  'bizimhesap.write_customers': {
    title: 'Add accounts',
    short: 'Opens an account in BizimHesap for a dealer that has none.',
  },
  'bizimhesap.write_cancel': {
    title: 'Cancel invoices',
    short:
      'Cancels the BizimHesap record of a fully cancelled order. An e-invoice needs a return instead (pending accountant confirmation).',
  },
  'bizimhesap.save_writes': {
    title: 'Save write settings',
    short: 'Requires two-step verification; audited.',
  },
  'bizimhesap.corrections': {
    title: 'Paid orders sent without a cash account',
    short: 'Open collection records for them.',
  },
  'bizimhesap.queue_corrections': {
    title: 'Open collection records',
    short: 'Nothing is sent to BizimHesap.',
  },
  'bizimhesap.collections': {
    title: 'Collection queue',
    short: 'Pending, sent, failed and uncertain collections.',
  },
  'bizimhesap.collection_actions': {
    title: 'Send · Check statement · Cancel record',
    short: 'Uncertain collections are checked by searching the statement for the collection number.',
  },
  'bizimhesap.cancel_note': { title: 'Reason', short: 'E.g. "entered in BizimHesap by hand". Audited.' },
  'bizimhesap.samples': {
    title: 'Response samples',
    short: 'First real responses with personal data masked.',
  },
  'page.sales_commissions': {
    title: 'Rep commissions',
    short: 'Sales rep commission rules and monthly statements: calculation, approval, paid record and CSV.',
  },
  'sales_commissions.rules': {
    title: 'Commission rules',
    short:
      'Each line uses the most specific rule: rep-specific before general, product before category, category before all products; ties go to the newest rule.',
  },
  'sales_commissions.rate': {
    title: 'Commission rate',
    short: 'Percentage of the net amount (at most 50%). Locked at the order’s first calculation.',
  },
  'sales_commissions.rule_rep': {
    title: 'Rep',
    short: 'Empty: applies to all reps. Chosen: only that rep’s orders.',
  },
  'sales_commissions.rule_scope': {
    title: 'Category and product scope',
    short: 'No categories or products chosen: applies to all products.',
  },
  'sales_commissions.period': {
    title: 'Monthly statements',
    short:
      'One statement per rep and month. Approve locks it (kept on recalculation); Mark as paid records the payment made outside the system.',
  },
  'sales_commissions.calculate': {
    title: 'Calculate period',
    short:
      'Calculates the month’s orders and the collection/refund changes of earlier orders; rebuilds draft statements.',
  },
  'sales_commissions.csv': {
    title: 'Download CSV',
    short: 'All statement lines of the period (order, type, collected share, base, commission).',
  },
  'sales_commissions.paid_note': {
    title: 'Payment note',
    short: 'How the payment was made (e.g. October payroll). Recorded in the audit log.',
  },
  'page.quotes': {
    title: 'Quotes',
    short: 'Quotes requested by dealers and prepared by you; status and validity.',
  },
  'page.quote_detail': { title: 'Quote', short: 'Lines, prices, validity and note; send, cancel and print.' },
  'quotes.new': { title: 'New quote', short: 'Opens an empty quote for an approved dealer.' },
  'quotes.status_filter': {
    title: 'Status',
    short: 'Requested, draft, sent, ordered, declined or cancelled.',
  },
  'quotes.search': { title: 'Search', short: 'Part of the quote number or dealer name.' },
  'quotes.dealer': { title: 'Dealer', short: 'Approved dealers only; prices follow the dealer group.' },
  'quotes.print': {
    title: 'Print',
    short: 'Prints the quote; save as PDF from the browser. The internal note is not printed.',
  },
  'quotes.save': {
    title: 'Save',
    short: 'Saves lines, prices and terms; a sent quote turns back into a draft.',
  },
  'quotes.send': {
    title: 'Send',
    short: 'Creates an immutable version the dealer sees under My account; emails if delivery is set up.',
  },
  'quotes.cancel': { title: 'Cancel', short: 'The quote can no longer be accepted; a reason is required.' },
  'quotes.valid_until': {
    title: 'Valid until',
    short: 'Last day the quote can be accepted; required to send.',
  },
  'quotes.note': { title: 'Note to dealer', short: 'Shown to the dealer on the quote.' },
  'quotes.internal_note': {
    title: 'Internal note',
    short: 'Panel only; not shown to the dealer or printed.',
  },
  'quotes.cancel_reason': {
    title: 'Cancel reason',
    short: 'At least 3 characters; kept in the panel and audit log.',
  },
  'page.ledger': {
    title: 'Dealer accounts',
    short: 'Dealer balances, overdue amounts and oldest due dates; opening balance import.',
  },
  'page.company_ledger': {
    title: 'Dealer account',
    short:
      'Balance, ageing and statement (brought forward and running balance); record payments and adjustments.',
  },
  'ledger.search': { title: 'Search dealers', short: 'Part of the company name or dealer code.' },
  'ledger.only_overdue': {
    title: 'Overdue only',
    short: 'Shows dealers with unpaid amounts past their due date.',
  },
  'ledger.import': {
    title: 'Import opening balances',
    short:
      'Import dealer balances from your previous accounting (CSV/Excel): dealer code or tax number, amount (debit positive, credit negative), optional currency, date, due date, description. Preview first.',
  },
  'page.ledger_bank_import': {
    title: 'Bank transactions',
    short: 'Upload your bank statement and post dealer payments to their accounts in bulk.',
  },
  'ledger.bank_import': {
    title: 'Upload bank transactions',
    short: 'Posts dealer payments from a bank statement as collections, after a preview.',
  },
  'ledger.bank_import_file': {
    title: 'Bank statement',
    short: 'CSV or XLSX with Date and Amount (or Credit/Debit). Outgoing transactions are skipped.',
  },
  'ledger.bank_import_dealer': {
    title: 'Dealer',
    short: 'Check the suggested dealer or choose one from the list.',
  },
  'ledger.bank_import_apply': {
    title: 'Post selected',
    short: 'Posts the selected transactions as bank transfer collections; a preview is applied once.',
  },
  'organization.system_name': {
    title: 'Panel name (system name)',
    short:
      'The name shown at the top of the admin panel, in the browser tab and on the sign-in screen. Leave empty for "Zootopia Panel".',
  },
  'integrations.verify_all': {
    title: 'Verify all connections',
    short:
      'Tests every saved connection now and shows the result. Only tests that send no messages, open no payments and cost nothing are run; the connection status does not change.',
  },
  'ledger.balance_payments': {
    title: 'Card balance payments',
    short: 'Card payments the dealer made from My account; verified payments are posted as card collections.',
  },
  'dashboard.dealer_payments': {
    title: 'Recent dealer payments',
    short:
      'Card payments dealers made from My account → Account statement in the last 30 days (latest 10). Each payment is posted to the dealer account automatically; the money goes to your payment provider account.',
  },
  'dashboard.dealer_payment_email': {
    title: 'Email on dealer payment',
    short:
      'When on, alert recipients (or owners and admins if none) get an email with the amount and payment key when a dealer pays by card. The Android notification is not affected.',
  },
  'ledger.import_apply': {
    title: 'Post to ledger',
    short:
      'Posts the valid preview rows as opening balances. Invalid rows are skipped; a file is applied once.',
  },
  'ledger.currency': { title: 'Currency', short: 'Each currency has its own ledger and balance.' },
  'ledger.period': {
    title: 'Period',
    short: 'Statement start and end dates; earlier entries are brought forward.',
  },
  'ledger.payment': {
    title: 'Record payment',
    short: 'Records money received from the dealer as a credit, applied to the oldest due debt.',
  },
  'ledger.adjustment': {
    title: 'Record adjustment',
    short: 'Manual debit or credit (e.g. shipping difference); a description is required.',
  },
  'ledger.reverse': {
    title: 'Reverse',
    short:
      'Cancels a manual entry with an opposite entry. Nothing is deleted; order entries follow the order.',
  },
  'ledger.statement_csv': { title: 'Download statement (CSV)', short: 'Statement of the selected period.' },
  'ledger.print': { title: 'Print', short: 'Print the statement or save it as PDF from the browser.' },
  'ledger.amount': {
    title: 'Amount',
    short: 'With decimals (e.g. 1,250.50). The direction comes from the type.',
  },
  'ledger.entry_date': {
    title: 'Entry date',
    short: 'The day of the payment or adjustment; not in the future.',
  },
  'ledger.method': {
    title: 'Payment method',
    short: 'Bank transfer, cash, cheque, promissory note, card or other.',
  },
  'ledger.reference': { title: 'Document no', short: 'Receipt, cheque or note number (optional).' },
  'ledger.direction': { title: 'Direction', short: 'Debit increases the dealer debt; credit decreases it.' },
  'ledger.due_date': { title: 'Due date', short: 'When a debit adjustment is due; the entry date if empty.' },
  'ledger.open_items': {
    title: 'Open items',
    short:
      'Unpaid amounts, oldest due date first. Payments and refunds are applied to the oldest due debt. The dealer sees the same list under My account.',
  },
  'ledger.reminders': {
    title: 'Payment reminders',
    short:
      'When on, every morning at 09:10 the dealer (company email and company owners) gets one email for items due soon or overdue. Each item is reminded at most once per stage. Off by default.',
  },
  'ledger.reminders_enabled': {
    title: 'Turn reminders on',
    short:
      'Turning this on sends real emails to your dealers; you are asked to tick a confirmation box. Nothing is sent without email delivery set up.',
  },
  'ledger.reminders_days': {
    title: 'Days before',
    short: 'How many days before the due date to remind (0–30). With 0, only overdue items are reminded.',
  },
  'ledger.reminders_save': {
    title: 'Save',
    short: 'Saves the reminder setting. The change is audited; the first run is the next morning at 09:10.',
  },
  'dealers.member_role': {
    title: 'Company role',
    short:
      'Owner: orders, sees company orders, the statement and users, manages locations. Buyer: orders and sees own orders; a threshold may apply. View only: sees prices, company orders and the statement; cannot order. A company keeps at least one owner.',
  },
  'dealers.order_limit': {
    title: 'Order threshold',
    short:
      'A buyer cannot place an order above this amount (shipping included); the storefront asks for owner approval. Empty: no threshold. Not applied to orders placed in the panel.',
  },
  'dealers.locations': {
    title: 'Locations',
    short:
      'Branch, delivery and billing addresses. Company users pick one at checkout; the order keeps a copy. The company owner can also add locations under My account.',
  },
  'dealers.location_label': {
    title: 'Location name',
    short: 'Short name shown in lists and at checkout (e.g. "Head office"); up to 40 characters.',
  },
  'orders.location_fill': {
    title: 'Fill from company location',
    short:
      'Pick one of the dealer customer’s company locations to fill the delivery address; you can still edit it.',
  },
  'dealers.credit_limit': {
    title: 'Credit limit',
    short: 'The open-account balance plus a new order cannot exceed this amount. Empty: no limit.',
  },
  'dealers.credit_currency': {
    title: 'Limit currency',
    short:
      'The limit applies to the balance in this currency; other currencies are blocked for open account.',
  },
  'dealers.block_when_overdue': {
    title: 'Block open account when overdue',
    short: 'New open-account orders are blocked while the dealer has overdue debt.',
  },
  'orders.credit_override': {
    title: 'Override reason',
    short:
      'The order exceeds the credit limit or the dealer is overdue. With a reason the order is placed anyway; recorded in the audit log.',
  },
  'dealers.payment_term': {
    title: 'Payment term (days)',
    short: 'Open-account orders are due this many days after the order date. Applies to new orders.',
  },
  'retention.rules': {
    title: 'Automatic destruction rules',
    short: 'Retention period and automatic deletion per data type. All off by default.',
  },
  'retention.days': { title: 'Retention period (days)', short: 'Records older than this are deleted.' },
  'retention.due': {
    title: 'Due today',
    short: 'Records that would be deleted tonight with the saved period.',
  },
  'retention.enabled': {
    title: 'Automatic destruction',
    short: 'When on, expired records are permanently deleted every night.',
  },
  'retention.save': {
    title: 'Save',
    short: 'Saves periods and on/off state. Turning a rule on asks for confirmation first.',
  },
  'retention.confirm': {
    title: 'Turn on and save',
    short: 'Rules you turn on permanently delete expired records from tonight.',
  },
  'retention.fixed': {
    title: 'Fixed retention periods',
    short: 'Each feature’s own clean-up period; also written to the destruction log.',
  },
  'retention.log': {
    title: 'Destruction log',
    short: 'How many records of which data type were deleted or anonymised on which day.',
  },
  'retention.log_period': { title: 'Period', short: 'Last 30, 90, 365 or 1095 days.' },
  'page.google_reports': {
    title: 'Google reports',
    short: 'GA4 channel/day report and Google Business Profile location details and reviews for the store.',
  },
  'google_reports.ga4': {
    title: 'Analytics (GA4) report',
    short: 'Read from the GA4 property linked to the store.',
  },
  'google_reports.period': { title: 'Period', short: 'Last 7, 28 or 90 days (up to yesterday).' },
  'google_reports.business_profile': {
    title: 'Google Business Profile',
    short: 'Location details and the latest 50 reviews (read only).',
  },
  'google_reports.reply': {
    title: 'Reply to reviews',
    short: 'Opens the location in Google Maps to reply there.',
  },
  'ads.google_conversions': {
    title: 'Google Ads conversion upload',
    short: 'Sends paid orders from ads (gclid, order no, value) to Google Ads. Off by default.',
  },
  'ads.google_conversions_enabled': {
    title: 'Upload conversions',
    short: 'Requires approval permission and two-step verification.',
  },
  'ads.google_conversion_action': {
    title: 'Conversion action',
    short: 'Resource name customers/<id>/conversionActions/<id>.',
  },
  'ads.google_conversions_save': { title: 'Save', short: 'Saves the setting; audited.' },
  'blog.title': { title: 'Post title', short: 'Shown on the storefront (max 160).' },
  'blog.slug': {
    title: 'Short name (address)',
    short: '/blog/<short-name>. On a live post it changes on save and redirects.',
  },
  'blog.excerpt': { title: 'Excerpt', short: 'Short text shown in the list (max 300).' },
  'blog.category': { title: 'Category', short: 'Optional blog category.' },
  'blog.tags': { title: 'Tags', short: 'Comma separated (max 10).' },
  'blog.author': { title: 'Author', short: 'Name and title shown on the post.' },
  'blog.cover': { title: 'Cover image', short: 'Optional; alt text is required.' },
  'blog.health_content': {
    title: 'Health content',
    short: 'Needs expert approval; a notice is shown on the storefront.',
  },
  'blog.references': { title: 'References', short: 'Sources with optional links (max 30).' },
  'blog.related': { title: 'Related products', short: 'Shown at the end of the post (max 8).' },
  'blog.preview': {
    title: 'Preview',
    short: 'Saves and opens the draft in a new tab (signed link, 2 hours).',
  },
  'blog.save': { title: 'Save draft', short: 'The storefront does not change until you publish again.' },
  'blog.feature': { title: 'Feature', short: 'Makes the post the featured post on the Articles page.' },
  'blog.publish': { title: 'Publish', short: 'Publishes the draft as a new version.' },
  'blog.schedule': { title: 'Publish date', short: 'Empty means publish now.' },
  'blog.unpublish': { title: 'Unpublish', short: 'Removes the post from the storefront.' },
  'blog.delete': { title: 'Delete post', short: 'Permanently deletes a never-published post.' },
  'blog.category_add': { title: 'Add category', short: 'Adds a blog category.' },
  'blog.category_edit': {
    title: 'Edit or delete category',
    short: 'Renaming redirects the old address; posts are kept.',
  },
  'menus.post': { title: 'Blog post', short: 'Shown in the menu only while the post is live.' },
  'page.product_bulk': {
    title: 'Bulk Action History',
    short: 'Bulk product changes, their results and skipped rows; applied actions can be reverted.',
  },
  'page.product_grid': {
    title: 'Edit in Table',
    short:
      'See products and variants in a table and edit SKU, barcode, status, price and stock like a spreadsheet.',
  },
  'page.reviews': {
    title: 'Product reviews',
    short: 'Approve, reply to or reject reviews from customers who bought and received the product.',
  },
  'page.review_detail': { title: 'Review', short: 'Approve, reply to or reject a review.' },
  'reviews.enabled': {
    title: 'Reviews shown in the storefront',
    short: 'When off, reviews are hidden and cannot be written.',
  },
  'reviews.filter': { title: 'Status', short: 'Pending, live or rejected reviews.' },
  'reviews.reply': { title: 'Store reply', short: 'Shown under the review with the store name (max 2000).' },
  'reviews.save_reply': { title: 'Save reply', short: 'Saves the reply; an empty reply removes it.' },
  'reviews.publish': { title: 'Approve and publish', short: 'Publishes the review on the product page.' },
  'reviews.reject': { title: 'Rejection reason', short: 'Visible only in the panel.' },
  'page.questions': {
    title: 'Product questions',
    short: 'Answer customer questions and publish them on product pages.',
  },
  'page.question_detail': { title: 'Question', short: 'Answer, publish or reject a question.' },
  'questions.filter': { title: 'Status', short: 'Awaiting answer, live or rejected questions.' },
  'questions.answer': { title: 'Answer', short: 'Shown with the question under the store name (max 2000).' },
  'questions.save': {
    title: 'Save answer',
    short: 'If the question is live, the storefront changes immediately.',
  },
  'questions.publish': {
    title: 'Publish',
    short: 'Shows the Q&A on the product page; the customer is emailed once.',
  },
  'questions.reject': {
    title: 'Reject / unpublish',
    short: 'Hides the question; the reason is only shown in the panel.',
  },
  'content.locale': {
    title: 'Content language',
    short: 'Shows content in the selected language; new items are created in this language.',
  },
  'content.translate': {
    title: 'Add translation',
    short: 'Copies the content as a draft in the chosen language and opens its editor.',
  },
  'content.translate_target': {
    title: 'Target language',
    short: 'Only store languages without a translation are listed.',
  },
  'blog.category_translation_name': {
    title: 'Category name (target language)',
    short: 'The category name in the target language; the address is generated from it.',
  },
  'menus.translation_draft': {
    title: 'Start from another language',
    short:
      'Loads the menu from another language as an unsaved draft, linking pages and posts to their translations.',
  },
  'pages.hero_text': {
    title: 'Text below heading',
    short: 'Short text below the large heading; checked for health claims.',
  },
  'pages.grid_category': { title: 'Category', short: 'The grid shows visible products from this category.' },
  'pages.post_limit': {
    title: 'Number of posts',
    short: 'How many of the newest published posts to show (1–12).',
  },
  'pages.add_section': {
    title: 'Add section',
    short: 'Adds a section of the selected type to the end of the page.',
  },
  'pages.faq_items': {
    title: 'Add / remove question',
    short: 'Adds or removes a question-answer pair in the FAQ section.',
  },
  'pages.review_decision': {
    title: 'Approve / reject',
    short: 'The veterinary expert approves the saved content or rejects it with a reason.',
  },
  'blog.category_name': {
    title: 'Category name',
    short: 'Shown as the blog category title on the storefront.',
  },
  'blog.category_slug': {
    title: 'Short name',
    short: 'Category address; the old address is redirected if changed.',
  },
  'blog.category_description': {
    title: 'Category description',
    short: 'Short text shown at the top of the category page.',
  },
  'editor.link_url': { title: 'Link address', short: 'A site path, an https:// address, mailto: or tel:.' },
  'editor.table': {
    title: 'Table',
    short:
      'With the cursor in a table you can add and delete rows and columns. The first row is the header row.',
  },
  'editor.video': {
    title: 'Video address',
    short: 'A YouTube or Vimeo address. Videos from other sites are not embedded.',
  },
  'editor.import': {
    title: 'Import Markdown or HTML',
    short: 'Converts text written elsewhere; scripts, styles, forms and external images are removed.',
  },
  'editor.image_alt': { title: 'Alternative text', short: 'Required short description of the image.' },
  'editor.image_caption': { title: 'Image caption', short: 'Optional caption shown below the image.' },
  'menus.translation_source': { title: 'Source language', short: 'The language of the menu to start from.' },
  'categories.name': {
    title: 'Category name',
    short: 'Shown in menus, on the category page and in product breadcrumbs.',
  },
  'categories.slug': {
    title: 'Short name (address)',
    short: 'The category address; the old address redirects (301) if changed.',
  },
  'categories.description': {
    title: 'Category description',
    short: 'Shown at the top of the category page; used as the search description fallback.',
  },
  'categories.seo_title': {
    title: 'Title in search results',
    short: 'Up to 70 characters; the category name is used if empty.',
  },
  'categories.seo_description': {
    title: 'Description in search results',
    short: 'Up to 170 characters; the category description is used if empty.',
  },
  'categories.edit': {
    title: 'Edit category',
    short: 'Changes the name, address, description and search result fields.',
  },
  'redirects.not_found': {
    title: 'Addresses not found',
    short: 'Lists storefront addresses returning 404 with how often they were seen.',
  },
  'redirects.not_found_filter': { title: 'Show', short: 'Shows pending or ignored addresses.' },
  'redirects.not_found_actions': {
    title: 'Redirect / 410 / Ignore',
    short: 'Redirect opens the redirect dialog; 410 marks it permanently removed; Ignore hides it.',
  },
  'redirects.filter_source': {
    title: 'Source',
    short:
      'Filters rules by origin: manual, import, automatic (address changed) or match (system-confirmed counterpart).',
  },
  'redirects.filter_host': {
    title: 'Domain',
    short:
      '"All domains" rules apply to the store domain and every old domain; pick an old domain to see its own rules.',
  },
  'redirects.host': {
    title: 'Source domain',
    short:
      'Leave empty to apply the rule on every domain (recommended); enter an old domain (without "www.") for a rule only there.',
  },
  'redirects.shadowed': {
    title: 'Rules hiding store pages',
    short:
      'The old address of these rules is a real storefront page, so visitors are redirected instead of seeing it. Delete the rule to show the page.',
  },
  'page.broken_links': {
    title: 'Broken links',
    short:
      'Storefront addresses returning 404 and old addresses sent to a safe page because no counterpart was found.',
    whatItDoes:
      'Unmatched old addresses (old domain or old site formats) are redirected with 301 to the species category, all products or the home page and listed here. Only the path and domain are stored.',
  },
  'broken_links.collect': {
    title: 'Collect old addresses from archive',
    short:
      'Fetches page addresses of the old domains from the Internet Archive (Wayback Machine) and matches them.',
  },
  'broken_links.host': {
    title: 'Domain',
    short: '"Store domain" is the store\'s own domain; the others are old domains.',
  },
  'broken_links.found_via': {
    title: 'Found via',
    short:
      'Visit: the address was opened. Archive: collected from the Wayback Machine. Search Console: seen in Google.',
  },
  'sc_setup.connect': {
    title: 'Connect to Google and set up',
    short:
      'The system sets up Search Console: properties of the store domain and old domains are verified and added, the sitemap is submitted and old pages seen in Google are mapped.',
  },
  'sc_setup.run': {
    title: 'Connect to Google / Run setup again',
    short:
      'Runs setup immediately when the connection and permissions are complete; otherwise opens the Google consent screen.',
  },
  'sc_setup.enable_api': {
    title: 'Enable the API in Google Cloud',
    short:
      'Setup uses two free Google APIs: the verification steps use the Site Verification API; adding properties, the sitemap and old pages use the Search Console API. Open the link in the Google Cloud project of the OAuth client you connected with, press Enable, then run the setup again after 1–2 minutes.',
  },
  'sc_setup.inspections': {
    title: 'Google index status',
    short:
      'Every morning live product pages and the most used old addresses are checked with Google URL inspection.',
  },
  'indexnow.section': {
    title: 'IndexNow (Bing, Yandex)',
    short:
      'Reports changed addresses to Bing and Yandex every ten minutes; all addresses are sent once on the first run.',
  },
  'indexnow.submit': {
    title: 'Send all addresses now',
    short: "Sends all of the store's indexable addresses to IndexNow.",
  },
  'system.staging_banner': {
    title: 'Staging environment',
    short: 'This panel runs on the staging server; e-mails go only to allow-listed addresses.',
  },
  'system.mail_allowlist': {
    title: 'E-mail allow list',
    short: 'Addresses or @domains that may receive e-mail on staging. Empty list blocks all e-mail.',
  },
  'system.mail_allowlist_save': {
    title: 'Save allow list',
    short: 'Saves the list; applies from the next e-mail. Recorded in the audit log.',
  },
  'products.media_retry': {
    title: 'Retry image',
    short: 'Queues the failed import image again; the background job retries in about 30 s.',
  },
  'imports.sku_generate': {
    title: 'Preview again with suggested SKUs',
    short:
      'Suggests SKUs for variants without one (barcode, or product code + option). Review before applying.',
  },
  'imports.media_retry_all': {
    title: 'Retry failed downloads',
    short: 'Queues all failed import images again.',
  },
  'imports.default_brand': {
    title: 'Default brand',
    short: 'New products in rows with an empty "Brand" column get this brand.',
  },
  'imports.categories_file': {
    title: 'Choose category file and preview',
    short: 'Columns: name, parent, description, order, slug, status, SEO title/description. Preview first.',
  },
  'imports.categories_apply': {
    title: 'Apply categories',
    short: 'Writes all additions and updates in one transaction, parents first.',
  },
  'imports.products_apply': {
    title: 'Import products',
    short:
      'Adds new products as drafts and updates existing ones; more than 50 products run in the background.',
  },
  'imports.dealers_apply': {
    title: 'Import dealers',
    short: 'Adds or updates the previewed dealers in one transaction and links their users.',
  },
  'imports.categories_export': {
    title: 'Template / export categories',
    short: 'Downloads the template or the current category tree with the same columns.',
  },
  'imports.create_missing_categories': {
    title: 'Create missing categories',
    short: 'Creates categories not found in the "Category" column, including parents in "A > B" paths.',
  },
  'imports.category_mode': {
    title: 'Categories of existing products',
    short: 'Add keeps current categories; Replace uses only those in the file.',
  },
  'stock.transfer_receipt': {
    title: 'Enter receipt',
    short: 'Opens the first stock entry (receipt with lot and expiry) instead of a transfer.',
  },
  'stores.archive': {
    title: 'Archive',
    short: 'Closes the storefront of an unused store and lists it last; nothing is deleted.',
  },
  'stores.delete': {
    title: 'Delete store',
    short:
      'Only a preview/archived store without orders; its domains, markets, price lists and content are deleted.',
  },
  'stores.delete_code': { title: 'Store code', short: 'Type the store code exactly to confirm.' },
  'products.price_list_missing': {
    title: 'Go to Pricing',
    short: "Storefront prices come from the store's active base price list (no group).",
  },
  'payments.card_status': {
    title: 'Card payment on the storefront',
    short:
      'Off, test (sandbox, no money) or live. The Integrations badge only shows the key connection test.',
  },
  'migration.export': {
    title: 'Download migration package',
    short: 'Downloads the selected store as a ZIP; no personal data or keys.',
  },
  'migration.target_store': {
    title: 'Target store',
    short: 'Store to apply to; empty matches the package store code or creates it.',
  },
  'migration.include_stock': {
    title: 'Opening stock',
    short: 'Writes current balances per warehouse/lot as opening stock.',
  },
  'migration.overwrite': {
    title: 'Overwrite',
    short: 'Off: only missing records are added. On: differing records are updated.',
  },
  'migration.legal_reviewed': {
    title: 'Lawyer review',
    short: 'Publishes legal texts with the reviewed flag in your name.',
  },
  'migration.carry_approvals': {
    title: 'Carry veterinary approvals',
    short: 'Records staging approvals in your name; otherwise items await approval.',
  },
  'migration.file': {
    title: 'Choose package and preview',
    short: 'Uploads and previews the ZIP; nothing is written.',
  },
  'migration.refresh': {
    title: 'Refresh preview',
    short: 'Recalculates the preview with the changed options.',
  },
  'migration.apply': { title: 'Import', short: 'Applies the previewed package in one transaction.' },
  'privacy.create': {
    title: 'Record request',
    short: 'Records a request received by email, KEP, post or in person.',
  },
  'privacy.requester_email': {
    title: "Requester's email",
    short: 'The outcome is sent here; accounts and guest orders match by this address.',
  },
  'privacy.received_at': { title: 'Request date', short: 'The 30-day deadline counts from this date.' },
  'privacy.export': {
    title: 'Download a copy of the data',
    short: "Downloads the person's records as JSON.",
  },
  'privacy.note': { title: 'Reply to requester', short: 'Emailed to the requester on resolve or reject.' },
  'privacy.start_review': { title: 'Start review', short: 'Marks the request as being handled.' },
  'privacy.complete': { title: 'Resolve', short: 'Closes the request with a reply.' },
  'privacy.reject': { title: 'Reject', short: 'Closes the request with a reason.' },
  'privacy.anonymize': {
    title: 'Anonymize',
    short: 'Permanently anonymizes personal data for an erasure request.',
  },
  'privacy.confirm_email': {
    title: 'Confirmation',
    short: "Type the requester's email exactly to enable the button.",
  },
  'system.error_tracking': {
    title: 'External error tracking',
    short: 'Unexpected server errors are sent to a Sentry-compatible service; no personal data.',
  },
  'system.alert_recipients': {
    title: 'Alert recipients',
    short: 'Addresses for critical alerts; owners if empty.',
  },
  'system.mail_dns': { title: 'Email domain check', short: 'SPF, DKIM and DMARC are read from DNS.' },
  'system.dkim_selector': { title: 'DKIM selector', short: 'The first part of the DKIM record name.' },
  'page.exports': {
    title: 'Exports',
    short: 'Download orders, order lines, customers, stock, invoices and payments as XLSX or CSV.',
  },
  'exports.period': {
    title: 'Period',
    short: 'Days in Turkey time; the end day is included. Stock is always current.',
  },
  'exports.from': { title: 'Start date', short: 'Records from 00:00 of this day (Turkey time).' },
  'exports.to': { title: 'End date', short: 'Records up to the end of this day.' },
  'exports.store': { title: 'Store', short: 'Limits orders, invoices and payments to one store.' },
  'exports.format': {
    title: 'File format',
    short: 'XLSX keeps amounts as numbers; CSV is semicolon separated.',
  },
  'exports.personal_data': {
    title: 'Personal data',
    short: 'Masked by default. Unmasked requires the personal data permission and two-step verification.',
  },
  'exports.orders': { title: 'Download orders', short: 'One row per order with totals and VAT.' },
  'exports.order_lines': { title: 'Download order lines', short: 'One row per item with VAT rate and lots.' },
  'exports.customers': {
    title: 'Download customers',
    short: 'Customers registered in the period, with consents.',
  },
  'exports.stock': {
    title: 'Download stock',
    short: 'Current stock by warehouse and lot, with expiry dates.',
  },
  'exports.invoices': {
    title: 'Download invoices',
    short: 'Invoices dated in the period with net, VAT and total.',
  },
  'exports.payments': { title: 'Download payments', short: 'Payments and refunds (negative) in date order.' },
  'stock.critical': {
    title: 'Low stock',
    short: 'Products whose sellable quantity is at or below their threshold.',
  },
  'stock.default_threshold': {
    title: 'Default threshold',
    short: 'Applies to products without their own threshold that have been stocked at least once.',
  },
  'stock.daily_digest': {
    title: 'Daily email summary',
    short: 'One email every morning at 08:00 (Turkey) when stock is low.',
  },
  'stock.thresholds': {
    title: 'Low stock thresholds',
    short: 'All warehouses (total) or per warehouse; empty means none.',
  },
  'stock.thresholds_save': {
    title: 'Save thresholds',
    short: "Saves this product's thresholds; the list updates at once.",
  },
  'page.seo': {
    title: 'SEO center',
    short: 'SEO issues of published products, categories, pages and posts.',
  },
  'seo.indexing': {
    title: 'Indexing',
    short: 'Open to search engines when the store is live and indexing is on.',
  },
  'seo.issues': {
    title: 'SEO issues',
    short:
      'Only published content is checked. Main images under 500 px (short side) are small; under 1500 px low resolution.',
  },
  'seo.issue_filter': { title: 'Issue type', short: 'Filters the list by one issue type.' },
  'seo.history': { title: 'SEO change history', short: 'Changes to SEO titles, descriptions and slugs.' },
  'seo.crawl': { title: 'Site crawler', short: 'Crawls your own storefront and finds technical SEO issues.' },
  'seo.crawl_start': {
    title: 'Start crawl',
    short: 'Queues a crawl; pages are fetched one per second, so 150 pages take a few minutes.',
  },
  'seo.crawl_filter': { title: 'Issue type (crawl)', short: 'Filters the latest crawl issues by type.' },
  'page.google': {
    title: 'Google connections',
    short:
      'Connects your Google account and picks Search Console, GA4 and Merchant Center resources per store.',
  },
  'google.client': {
    title: 'OAuth client',
    short: 'Created in Google Cloud Console; entered under Integrations.',
  },
  'google.redirect_uri': {
    title: 'Authorized redirect URI',
    short: 'Add this exact address to the OAuth client in Google Cloud.',
  },
  'google.connect': {
    title: 'Connect with Google',
    short: 'Opens Google’s consent screen for the selected services.',
  },
  'google.services': {
    title: 'Services and permissions',
    short: 'Search Console, Analytics (read only), Merchant Center.',
  },
  'google.test': { title: 'Test connection', short: 'Refreshes the token and checks the linked resources.' },
  'google.disconnect': {
    title: 'Remove connection',
    short: 'Revokes access at Google and deletes the token and links.',
  },
  'google.resources': {
    title: 'Store resources',
    short: 'Chosen from what the connected account can access.',
  },
  'google.logs': { title: 'Log', short: 'Connection, token, list, test and revoke operations.' },
  'page.search_console': {
    title: 'Search performance',
    short:
      'Clicks, impressions, CTR and average position in Google Search by query, page, country and device.',
  },
  'sc.sync': { title: 'Sync now', short: 'Fetches the last 28 days the first time, then the last 5 days.' },
  'sc.metrics': { title: 'Metrics', short: 'Clicks, impressions, click-through rate and average position.' },
  'sc.dimension': { title: 'Details table', short: 'Top 100 rows by clicks; filter with "Search".' },
  'sc.movers': {
    title: 'Rising and falling pages',
    short: 'Clicks compared with the previous equal period.',
  },
  'sc.sitemaps': { title: 'Sitemaps', short: 'Sitemaps in Search Console and their status.' },
  'sc.sitemap_submit': {
    title: 'Submit sitemap',
    short: 'Submits the store sitemap index to Search Console.',
  },
  'sc.inspect': {
    title: 'URL inspection',
    short: 'Google index status of a page; 2,000 per property per day.',
  },
  'sc.digest': { title: 'Weekly email summary', short: 'Sent to alert recipients every Monday.' },
  'page.merchant': {
    title: 'Google Shopping',
    short: 'Sends products to Google Merchant Center: pre-feed check, feed file and Merchant API.',
  },
  'merchant.preview': {
    title: 'Pre-feed check',
    short:
      'Products with health issues, no image or no price are not sent. Images below Google’s minimum size are skipped; at least 1500×1500 px is recommended.',
  },
  'merchant.feed': { title: 'Feed file', short: 'A secret address for Merchant Center scheduled fetch.' },
  'merchant.feed_rotate': {
    title: 'Renew address',
    short: 'Creates a new address; the old one stops working.',
  },
  'merchant.api': {
    title: 'Merchant API',
    short: 'Sends products every morning with the Google connection.',
  },
  'merchant.sync': {
    title: 'Send now',
    short: 'Sends changed products, deletes removed ones, updates statuses.',
  },
  'merchant.performance': {
    title: 'Product performance',
    short: 'Merchant Center report: free listing and Shopping ad impressions and clicks per product.',
  },
  'merchant.performance_period': { title: 'Period', short: 'Last 7, 28 or 90 days (up to yesterday).' },
  'merchant.issues': {
    title: 'Issues at Google',
    short: 'Disapproved or warned products with Google’s reasons.',
  },
  'products.merchant': {
    title: 'Google Shopping',
    short: 'Whether the product is sent to Merchant Center, and Google fields.',
  },
  'products.google_category': {
    title: 'Google product category',
    short: 'Google taxonomy number or full path.',
  },
  'products.identifier_exists': {
    title: 'Manufacturer barcode',
    short: 'Choose "no barcode" only if the product has none.',
  },
  'products.sku_is_mpn': {
    title: 'SKU = MPN',
    short: 'Turn on only for your own products: the SKU is sent to Google as the MPN.',
  },
  'products.custom_labels': {
    title: 'Custom labels',
    short: 'Free labels for grouping products in Google Ads.',
  },
  'page.attribution': {
    title: 'Campaigns and sources',
    short: 'Builds UTM campaign links and reports orders by source.',
  },
  'attribution.builder': {
    title: 'Campaign link',
    short: 'Source, medium and campaign; the link is only built on screen.',
  },
  'attribution.url': { title: 'Page address', short: 'The storefront page the visitor lands on.' },
  'attribution.source': {
    title: 'Source',
    short: 'Where traffic comes from: google, instagram, newsletter…',
  },
  'attribution.medium': { title: 'Medium', short: 'Traffic type: cpc, social, email, sms, qr…' },
  'attribution.campaign': { title: 'Campaign', short: 'Campaign name; rows are grouped by it.' },
  'attribution.term': { title: 'Keyword', short: 'Optional: paid search keyword.' },
  'attribution.content': { title: 'Content', short: 'Optional: tells apart links in the same campaign.' },
  'attribution.report': {
    title: 'Orders by source',
    short: 'Orders, paid revenue, refunds and net by source.',
  },
  'attribution.model': {
    title: 'Touch',
    short: 'Last touch: last non-direct source before the order. First touch: first visit.',
  },
  'page.web_vitals': {
    title: 'Page speed',
    short: 'Measures storefront speed with PageSpeed Insights (Core Web Vitals and Lighthouse score).',
  },
  'web_vitals.run': {
    title: 'Measure now',
    short: 'Eight measurements (four pages × mobile/desktop); takes a few minutes.',
  },
  'web_vitals.results': {
    title: 'Results',
    short: 'LCP: largest content time. INP: response to clicks. CLS: layout shift.',
  },
  'page.attributes': {
    title: 'Product attributes',
    short: 'Technical attribute definitions (number, option, yes/no, text) and tags in use.',
  },
  'attributes.list': {
    title: 'Attribute list',
    short: 'Type, options, where shown and products with a value.',
  },
  'attributes.code': {
    title: 'Short code',
    short: 'Used as the import column and storefront filter address.',
  },
  'attributes.type': { title: 'Attribute type', short: 'Cannot change once products have values.' },
  'attributes.options': {
    title: 'Options',
    short: 'One per line; options used by products cannot be removed.',
  },
  'attributes.visible': {
    title: 'Show on product page',
    short: 'Shown in the "Specifications" table and in product data for search engines.',
  },
  'attributes.filterable': { title: 'Show in storefront filters', short: 'Lets customers filter by it.' },
  'attributes.position': { title: 'Order', short: 'Smaller numbers come first.' },
  'products.tags': {
    title: 'Tags',
    short:
      'Comma-separated, stored lower-case; not shown in the storefront. Used by collection rules and filters.',
  },
  'products.copy': {
    title: 'Copy',
    short: 'Creates a draft copy with new SKUs; listing and stock are not copied.',
  },
  'products.save_template': {
    title: 'Save as template',
    short: 'Saves reusable fields of this product as a template.',
  },
  'products.template': { title: 'Template', short: 'Fills the new product form with the template values.' },
  'products.sku_suggest': {
    title: 'Suggest SKUs',
    short: 'Fills empty SKUs using the SKU pattern; always unique.',
  },
  'products.sku_pattern': {
    title: 'SKU pattern',
    short: 'Tokens: {marka} {kategori} {urun} {secenek} {sira}.',
  },
  'products.schedule': {
    title: 'Schedule publishing',
    short: 'Publishes the product in this store at a future time.',
  },
  'products.bulk': {
    title: 'Bulk action',
    short:
      'Changes status, categories, tags, collection, stock, SEO, Google Shopping or store publishing for many products at once, after a preview.',
  },
  'products.bulk_select_all': {
    title: 'Select all matching',
    short: 'Selects every product matching the search and filters (up to 5,000), not just this page.',
  },
  'products.bulk_kind': {
    title: 'Action',
    short: 'The change to make. Applying needs the bulk apply permission and two-step verification.',
  },
  'products.bulk_store': {
    title: 'Store',
    short:
      'The store to publish in or unpublish from. Only active, vet-approved (where required) products with a base price are published; others are skipped with the reason.',
  },
  'products.bulk_stock': {
    title: 'Stock quantity',
    short: 'Set every active variant to a count, or add/remove units. Recorded as stock movements.',
  },
  'products.bulk_seo': {
    title: 'SEO pattern',
    short: 'Tokens: {urun} product, {marka} brand, {kategori} first category.',
  },
  'products.bulk_preview': {
    title: 'Preview',
    short: 'Shows before and after values and skipped products with reasons. Nothing changes.',
  },
  'products.bulk_apply': {
    title: 'Apply',
    short: 'Makes the previewed changes; rows changed since the preview are left untouched.',
  },
  'products.bulk_history': {
    title: 'Bulk action history',
    short: 'The last 50 bulk actions with their results; applied actions can be reverted.',
  },
  'products.bulk_revert': {
    title: 'Revert',
    short: 'Restores the previous values; rows changed by hand since then are skipped.',
  },
  'products.grid': {
    title: 'Edit in table',
    short: 'Opens products as variant rows to edit SKU, barcode, status, price and stock like a spreadsheet.',
  },
  'products.grid_price_list': {
    title: 'Price list',
    short: 'The price column shows and edits the single-unit price in this list.',
  },
  'products.grid_warehouse': {
    title: 'Warehouse',
    short: 'The stock column shows and edits on-hand units in this warehouse.',
  },
  'products.grid_save': {
    title: 'Save changes',
    short: 'Shows a preview with conflicts and invalid cells first; saved changes can be reverted.',
  },
  'products.grid_discard': { title: 'Discard', short: 'Removes all unsaved cell changes.' },
  'stock.lot_pick': {
    title: 'Lot',
    short:
      'Choose the lot the count, adjustment or damage applies to; "Stock without lot" for the lotless balance.',
  },
  'imports.column_map': {
    title: 'Column mapping',
    short: 'Which field a column goes to; saved when applied and reused for the same headers.',
  },
  'imports.retry': { title: 'Retry failed products', short: 'Re-applies only the products that failed.' },
  'pricing.cost_based': {
    title: 'Cost-based price',
    short: 'Builds prices from cost with a markup or a target gross margin.',
  },
  'pricing.min_margin': {
    title: 'Minimum margin',
    short: 'Flags (or skips) products whose margin would drop below this.',
  },
  'pricing.fx_rate': {
    title: 'Rate',
    short: 'Converts the source list price with this manually entered rate.',
  },
  'pricing.campaign_dates': { title: 'Valid dates', short: 'The list gives prices only within this period.' },
  'products.price_tiers': { title: 'Quantity prices', short: 'Unit price from a minimum quantity.' },
  'products.extras': {
    title: 'Videos, documents and complementary products',
    short: 'Videos, PDF documents and "Goes well with" products shown on the product page.',
  },
  'products.videos': {
    title: 'Videos',
    short:
      'Paste a YouTube or Vimeo address and a title (up to 5). Nothing loads until the visitor plays it.',
  },
  'products.video_add': {
    title: 'Add video',
    short: 'Adds a video row. Enter the address and title, then save.',
  },
  'products.documents': {
    title: 'Documents',
    short: 'PDF documents such as a certificate of analysis (up to 10), publicly linked on the product page.',
  },
  'products.document_upload': {
    title: 'Upload PDF document',
    short: 'Up to 10 MB. PDFs with JavaScript, embedded files or launch actions are rejected.',
  },
  'products.complementary': {
    title: 'Complementary products',
    short: 'Products recommended with this one (up to 12), shown as "Goes well with".',
  },
  'products.attributes': {
    title: 'Product attributes',
    short: 'Values of the defined attributes for this product; empty ones are not stored.',
  },
  'page.collections': { title: 'Collections', short: 'Hand-picked or rule-based product groups.' },
  'page.collection_editor': {
    title: 'Collection',
    short: 'Name, address, status and products of a collection.',
  },
  'collections.slug': {
    title: 'Slug',
    short: 'Storefront address /koleksiyon/<slug>; old address is redirected.',
  },
  'collections.status': { title: 'Status', short: 'Hidden collections are not shown in the storefront.' },
  'collections.kind': { title: 'Hand-picked or rule', short: 'Rule-based collections update themselves.' },
  'collections.manual': {
    title: 'Add product',
    short: 'Search by name, SKU or barcode; up to 500 products.',
  },
  'collections.match': { title: 'Combining conditions', short: 'All conditions, or any one of them.' },
  'collections.preview': { title: 'Show matches', short: 'Lists matching products without saving.' },
  'page.site_search': {
    title: 'Site search',
    short: 'Most searched and zero-result searches; synonym groups.',
  },
  'site_search.report': {
    title: 'Search report',
    short: 'The first view of a search page counts; suggestions do not. No personal data; 90-day retention.',
  },
  'site_search.zero': {
    title: 'Searches without results',
    short: 'Add a synonym or the word to product names.',
  },
  'site_search.synonyms': { title: 'Synonyms', short: 'Each word in a group is searched for the others.' },
  'site_search.terms': { title: 'Words', short: 'Two to 20 single words, comma-separated.' },
  'pages.grid_collection': {
    title: 'Collection',
    short: 'One of the collections under Catalog → Collections.',
  },
  'pages.grid_brand': {
    title: 'Brand',
    short: 'Published products of the brand; "See all" opens the brand page.',
  },
  'page.keywords': {
    title: 'Keywords',
    short: 'Pool of target searches with Search Console metrics and alerts.',
  },
  'keywords.pool': {
    title: 'Keyword pool',
    short: 'Stored lower-case; one entry per keyword. Last 28 days of Search Console.',
  },
  'keywords.alerts': { title: 'Alerts', short: 'Cannibalization and content gaps.' },
  'keywords.intent': {
    title: 'Search intent',
    short: 'Informational, commercial, transactional or navigational.',
  },
  'keywords.cluster': { title: 'Cluster', short: 'Groups keywords on the same topic.' },
  'keywords.target': {
    title: 'Target page',
    short: 'The storefront path that should rank for this keyword.',
  },
  'keywords.candidates': {
    title: 'Search Console suggestions',
    short: 'Top 100 searches not yet in the pool.',
  },
  'keywords.rank_tracking': { title: 'Rank tracking', short: 'Needs a licensed (paid) data provider.' },
  'page.tracking': {
    title: 'Measurement',
    short: 'Adds GTM, GA4, Google Ads, Meta and TikTok tags to the storefront and manages cookie consent.',
  },
  'tracking.status': {
    title: 'Storefront status',
    short: 'Active when a tag is set and the cookie policy is published.',
  },
  'tracking.gtm': { title: 'Google Tag Manager ID', short: 'GTM-XXXXXXX. Loads only after consent.' },
  'tracking.ga4': { title: 'GA4 measurement ID', short: 'G-XXXXXXXXXX. Loads only with analytics consent.' },
  'tracking.google_ads': {
    title: 'Google Ads ID',
    short: 'AW-123456789. Loads only with marketing consent.',
  },
  'tracking.google_ads_label': {
    title: 'Purchase conversion label',
    short: 'The part after "AW-…/" in the conversion tag settings.',
  },
  'tracking.meta': { title: 'Meta pixel ID', short: 'Digits only. Loads only with marketing consent.' },
  'tracking.tiktok': { title: 'TikTok pixel ID', short: 'Loads only with marketing consent.' },
  'tracking.server_events': {
    title: 'Server-side events',
    short: 'Paid orders and refunds are sent from the server to GA4, Meta and TikTok, respecting consent.',
  },
  'tracking.server_ga4': {
    title: 'GA4 Measurement Protocol',
    short: 'GA4 → Admin → Data streams → Measurement Protocol API secrets → Create.',
  },
  'tracking.server_meta': {
    title: 'Meta Conversions API',
    short: 'Events Manager → pixel → Settings → Conversions API → Generate access token.',
  },
  'tracking.server_tiktok': {
    title: 'TikTok Events API',
    short: 'TikTok Ads Manager → Events → Web events → pixel → Settings → Generate access token.',
  },
  'tracking.server_test_code': {
    title: 'Test event code',
    short: 'Used only by the “Send test event” button.',
  },
  'tracking.server_test': {
    title: 'Send test event',
    short: 'GA4: validation endpoint; Meta/TikTok: a page view with the test code.',
  },
  'tracking.server_user_data': {
    title: 'Personal data matching',
    short: 'Email and phone are sent as SHA-256 hashes. Off by default.',
  },
  'tracking.server_log': {
    title: 'Server event log',
    short: 'Sent data, status and provider errors; failed events can be retried.',
  },
  'tracking.events': {
    title: 'Event manager',
    short:
      'Choose which standard events go to which service and define custom events. Consent rules are unchanged.',
  },
  'tracking.events_standard': {
    title: 'Standard events',
    short:
      'Untick to stop sending that event to that service. Events without a Meta/TikTok equivalent are never sent (—).',
  },
  'tracking.custom_events': {
    title: 'Custom events',
    short:
      'Up to 20 custom events: a page view or clicks on phone, email, WhatsApp or outbound links. No personal data.',
  },
  'tracking.custom_event_name': {
    title: 'Event name',
    short:
      'Lowercase start; a–z, digits and underscore, 3–40 characters. Standard and reserved names are not allowed.',
  },
  'tracking.custom_event_trigger': { title: 'When', short: 'Page view or click on a link type.' },
  'tracking.custom_event_path': {
    title: 'Page path',
    short: 'Exact path, or end with * to match the beginning.',
  },
  'tracking.custom_event_destinations': {
    title: 'Send to',
    short: 'Services that receive the event (only with the tag set and visitor consent).',
  },
  'tracking.custom_event_add': {
    title: 'Add to list',
    short: 'Adds the event; save to apply on the storefront.',
  },
  'tracking.custom_event_remove': { title: 'Remove event', short: 'Removes the event; save to apply.' },
  'tracking.events_save': { title: 'Save event settings', short: 'Applies event choices and custom events.' },
  'tracking.save': { title: 'Save tags', short: 'An empty field removes that tag.' },
  'tracking.legal': {
    title: 'Legal text impact',
    short: 'Cookies and recipients of the configured tags, checked against the published texts.',
  },
  'tracking.stats': {
    title: 'Cookie consent statistics',
    short: 'Choices in the last 30 days (latest choice per browser).',
  },
  'stores.market_tax_mode': {
    title: 'Tax mode',
    short:
      'Which VAT applies to orders in this market: domestic VAT, export exemption (no VAT) or destination-country VAT.',
  },
  'pricing.tax_country_rate': {
    title: 'Country VAT rates',
    short:
      'The rate of a tax class in a country; applied in markets whose tax mode is "Destination country VAT".',
  },
  'shipping.countries': {
    title: 'Destination countries',
    short: 'Market countries where the method is offered; if none is selected it is offered everywhere.',
  },
  'products.customs': {
    title: 'Customs and export',
    short: 'HS code, country of origin and customs description used on the proforma invoice and declaration.',
  },
  'products.hs_code': {
    title: 'HS code (GTİP)',
    short: 'Customs tariff position of the goods (6–12 digits).',
  },
  'products.origin_country': { title: 'Country of origin', short: 'Country where the goods were produced.' },
  'products.customs_description': {
    title: 'Customs description',
    short: 'Short, plain goods description for the declaration.',
  },
  'orders.tax_exemption': {
    title: 'Export — VAT exempt',
    short: 'The order came from a market with the export tax mode; no VAT was charged.',
  },
  'orders.proforma': {
    title: 'Proforma invoice',
    short: 'Printable order document for customs and the buyer; not a commercial invoice.',
  },
  'payments.bank_account_currency': {
    title: 'Account currency',
    short: 'Bank transfer is offered with this account only for orders in this currency.',
  },
  'page.order_proforma': {
    title: 'Proforma invoice',
    short: 'Proforma invoice of the order; print or save as PDF.',
  },
  'products.translations': {
    title: 'Translations',
    short:
      'Product name, descriptions and SEO fields in other languages, published at /en/… style addresses.',
  },
  'translations.seo': {
    title: 'SEO fields in a translation',
    short: 'Title (70 characters) and description (170 characters) shown in search results in that language.',
  },
  'keywords.language': {
    title: 'Keyword language',
    short: 'The language the keyword is searched in; the pool can be filtered by language.',
  },
  'merchant.markets': {
    title: 'Markets sent to Google',
    short: 'Each market (country + language + currency) is a separate feed and Merchant API data source.',
  },
  'payments.intl': {
    title: 'Foreign payments (Stripe, PayPal)',
    short:
      'Card payments in foreign currencies via Stripe and PayPal payments on the provider’s secure page.',
  },
  'payments.intl_entity': {
    title: 'Foreign company required',
    short:
      'Stripe and PayPal do not open accounts for businesses in Türkiye; use your company in a supported country.',
  },
  'payments.intl_card_provider': {
    title: 'Foreign-currency card payments',
    short:
      'With Stripe selected, non-TRY card payments go through Stripe; TRY payments use the domestic provider.',
  },
  'payments.paypal': {
    title: 'Pay with PayPal',
    short: 'Shown at checkout only for EUR, USD and GBP markets.',
  },
  'payments.intl_notify_urls': {
    title: 'Stripe and PayPal webhook URLs',
    short: 'Add these in Stripe Dashboard → Webhooks and PayPal Developer → app → Webhooks.',
  },
  'page.ui_translations': {
    title: 'Translations',
    short: 'Translation status of interface and help texts by language.',
  },
  'ui_translations.coverage': {
    title: 'Translation coverage',
    short: 'How many interface and help texts are translated in each language; 0 missing means complete.',
  },
  'page.subscriptions': {
    title: 'Repeat orders',
    short:
      'Regular deliveries customers start "every N weeks": settings, readiness and the subscription list.',
  },
  'subscriptions.settings': {
    title: 'Repeat order settings',
    short: 'Per store; off by default. Turning on needs a card payment provider and the contract texts.',
  },
  'subscriptions.enabled': {
    title: 'Turn on repeat delivery',
    short:
      'When on, eligible product pages show a "Deliver regularly" box. When off, no new period orders are opened; subscriptions wait.',
  },
  'subscriptions.intervals': {
    title: 'Intervals',
    short: 'Delivery frequencies (weeks) customers can choose. Pick at least one.',
  },
  'subscriptions.discount': {
    title: 'Subscription discount',
    short: 'Percentage off repeat orders (0–30). The rate at the start of a subscription stays fixed for it.',
  },
  'subscriptions.link_hours': {
    title: 'Payment link validity',
    short:
      'Time to pay a period order (24–168 hours). When it runs out the order is cancelled and stock released.',
  },
  'subscriptions.save': {
    title: 'Save settings',
    short: 'Saves the settings; asks for confirmation when turning on. The change goes to the audit log.',
  },
  'subscriptions.list': {
    title: 'Subscriptions',
    short: 'Customer, product, quantity, interval, next order day, status and the order awaiting payment.',
  },
  'subscriptions.runs': {
    title: 'Periods',
    short: 'Each period of the subscription: order opened, paid, skipped, out of stock, not paid.',
  },
  'subscriptions.cancel': {
    title: 'Cancel subscription',
    short:
      'Reason of at least 3 characters, written to the audit log. The customer gets no email; My account shows "Cancelled".',
  },
  'products.subscribable': {
    title: 'Available for repeat delivery',
    short:
      'When on and repeat delivery is enabled in the store, the product page shows a "Deliver regularly" box.',
  },
  'tracking.cross_domain': {
    title: 'Cross-domain tracking',
    short:
      'With several storefront domains, the GA4 linker and source data are carried between them; turns on by itself when a second domain is added.',
  },
  'shipping.geliver': {
    title: 'Shipping with Geliver',
    short:
      'Create shipments from orders with your Geliver account, compare carrier prices and buy the label in the panel. Connect under Integrations → Shipping → Geliver.',
  },
  'shipping.geliver_sender': {
    title: 'Sender address',
    short:
      'Where the parcel is collected. Chosen from the sender addresses defined in Geliver; the list is read from Geliver.',
  },
  'shipping.geliver_parcel': {
    title: 'Default parcel size',
    short:
      'Length, width and height suggested in the shipment window (cm, 1–300). Can be changed for each shipment.',
  },
  'shipping.geliver_weight': {
    title: 'Default weight',
    short:
      'Weight (grams) used for lines whose product has no weight. Entering product weights makes offers accurate.',
  },
  'shipping.geliver_save': {
    title: 'Save Geliver settings',
    short:
      'Saves the sender address and default parcel; a sender address missing from Geliver is not saved. The change is audit logged.',
  },
  'orders.ship_geliver': {
    title: 'Ship with Geliver',
    short:
      "Choose the packed products, parcel size and weight, get the carriers' offers from Geliver and buy the label with the chosen offer.",
  },
  'orders.geliver_parcel': {
    title: 'Parcel size and weight',
    short:
      'Size (cm) and weight (grams) that set the shipping price. The weight is suggested from product weights; weigh the parcel and correct it.',
  },
  'orders.geliver_offers': {
    title: 'Get offers',
    short:
      'Creates the shipment in Geliver and fetches carrier prices; nothing is charged. Fetch again if the size or products change.',
  },
  'orders.geliver_offer': {
    title: 'Shipping offer',
    short:
      'Carrier, service, estimated time and the price reported by Geliver. Cheapest and fastest are marked.',
  },
  'orders.geliver_buy': {
    title: 'Buy label',
    short:
      'Buys the label with the chosen offer; after confirmation the fee is charged to the Geliver balance (a test shipment in the test environment). The same offer is never bought twice.',
  },
  'orders.geliver_label': {
    title: 'Open label',
    short:
      'Opens the shipping label (PDF) produced by Geliver in a new tab; print it and stick it on the parcel.',
  },
  'orders.geliver_sync': {
    title: 'Refresh status',
    short:
      'Reads the shipment status and tracking number from Geliver now (it is read automatically every 30 minutes). The customer is emailed when the tracking number first arrives.',
  },
  'orders.geliver_cancel': {
    title: 'Cancel in Geliver',
    short:
      "Cancels the shipment in Geliver before the carrier collects it; packed products return to stock and the order can be shipped again. The label fee is refunded under Geliver's rules.",
  },
  'stores.customer_auth': {
    title: 'Customer sign-in',
    short:
      '"Continue with Google" on the storefront: sign-in, registration and checkout. Turned on per store; off by default.',
  },
  'stores.customer_auth_toggle': {
    title: 'Turn sign-in with Google on/off',
    short:
      'Only the business owner can turn it on, with two-step verification; the Sign in with Google client must be set up under Integrations. Turning off stops new Google sign-ins.',
  },
  'stores.customer_auth_redirects': {
    title: 'Redirect URIs',
    short:
      'Enter these addresses for each storefront domain in Google Cloud → APIs & Services → Credentials → OAuth client → Authorized redirect URIs.',
  },
};
